/**
 * Get the type of a JSON value.
 * Distinguishes between array, null and object.
 */
function typeofJsonValue(value) {
    let t = typeof value;
    if (t == "object") {
        if (Array.isArray(value))
            return "array";
        if (value === null)
            return "null";
    }
    return t;
}
/**
 * Is this a JSON object (instead of an array or null)?
 */
function isJsonObject(value) {
    return value !== null && typeof value == "object" && !Array.isArray(value);
}

// lookup table from base64 character to byte
let encTable = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'.split('');
// lookup table from base64 character *code* to byte because lookup by number is fast
let decTable = [];
for (let i = 0; i < encTable.length; i++)
    decTable[encTable[i].charCodeAt(0)] = i;
// support base64url variants
decTable["-".charCodeAt(0)] = encTable.indexOf("+");
decTable["_".charCodeAt(0)] = encTable.indexOf("/");
/**
 * Decodes a base64 string to a byte array.
 *
 * - ignores white-space, including line breaks and tabs
 * - allows inner padding (can decode concatenated base64 strings)
 * - does not require padding
 * - understands base64url encoding:
 *   "-" instead of "+",
 *   "_" instead of "/",
 *   no padding
 */
function base64decode(base64Str) {
    // estimate byte size, not accounting for inner padding and whitespace
    let es = base64Str.length * 3 / 4;
    // if (es % 3 !== 0)
    // throw new Error('invalid base64 string');
    if (base64Str[base64Str.length - 2] == '=')
        es -= 2;
    else if (base64Str[base64Str.length - 1] == '=')
        es -= 1;
    let bytes = new Uint8Array(es), bytePos = 0, // position in byte array
    groupPos = 0, // position in base64 group
    b, // current byte
    p = 0 // previous byte
    ;
    for (let i = 0; i < base64Str.length; i++) {
        b = decTable[base64Str.charCodeAt(i)];
        if (b === undefined) {
            // noinspection FallThroughInSwitchStatementJS
            switch (base64Str[i]) {
                case '=':
                    groupPos = 0; // reset state when padding found
                case '\n':
                case '\r':
                case '\t':
                case ' ':
                    continue; // skip white-space, and padding
                default:
                    throw Error(`invalid base64 string.`);
            }
        }
        switch (groupPos) {
            case 0:
                p = b;
                groupPos = 1;
                break;
            case 1:
                bytes[bytePos++] = p << 2 | (b & 48) >> 4;
                p = b;
                groupPos = 2;
                break;
            case 2:
                bytes[bytePos++] = (p & 15) << 4 | (b & 60) >> 2;
                p = b;
                groupPos = 3;
                break;
            case 3:
                bytes[bytePos++] = (p & 3) << 6 | b;
                groupPos = 0;
                break;
        }
    }
    if (groupPos == 1)
        throw Error(`invalid base64 string.`);
    return bytes.subarray(0, bytePos);
}
/**
 * Encodes a byte array to a base64 string.
 * Adds padding at the end.
 * Does not insert newlines.
 */
function base64encode(bytes) {
    let base64 = '', groupPos = 0, // position in base64 group
    b, // current byte
    p = 0; // carry over from previous byte
    for (let i = 0; i < bytes.length; i++) {
        b = bytes[i];
        switch (groupPos) {
            case 0:
                base64 += encTable[b >> 2];
                p = (b & 3) << 4;
                groupPos = 1;
                break;
            case 1:
                base64 += encTable[p | b >> 4];
                p = (b & 15) << 2;
                groupPos = 2;
                break;
            case 2:
                base64 += encTable[p | b >> 6];
                base64 += encTable[b & 63];
                groupPos = 0;
                break;
        }
    }
    // padding required?
    if (groupPos) {
        base64 += encTable[p];
        base64 += '=';
        if (groupPos == 1)
            base64 += '=';
    }
    return base64;
}

/**
 * This handler implements the default behaviour for unknown fields.
 * When reading data, unknown fields are stored on the message, in a
 * symbol property.
 * When writing data, the symbol property is queried and unknown fields
 * are serialized into the output again.
 */
var UnknownFieldHandler;
(function (UnknownFieldHandler) {
    /**
     * The symbol used to store unknown fields for a message.
     * The property must conform to `UnknownFieldContainer`.
     */
    UnknownFieldHandler.symbol = Symbol.for("protobuf-ts/unknown");
    /**
     * Store an unknown field during binary read directly on the message.
     * This method is compatible with `BinaryReadOptions.readUnknownField`.
     */
    UnknownFieldHandler.onRead = (typeName, message, fieldNo, wireType, data) => {
        let container = is(message) ? message[UnknownFieldHandler.symbol] : message[UnknownFieldHandler.symbol] = [];
        container.push({ no: fieldNo, wireType, data });
    };
    /**
     * Write unknown fields stored for the message to the writer.
     * This method is compatible with `BinaryWriteOptions.writeUnknownFields`.
     */
    UnknownFieldHandler.onWrite = (typeName, message, writer) => {
        for (let { no, wireType, data } of UnknownFieldHandler.list(message))
            writer.tag(no, wireType).raw(data);
    };
    /**
     * List unknown fields stored for the message.
     * Note that there may be multiples fields with the same number.
     */
    UnknownFieldHandler.list = (message, fieldNo) => {
        if (is(message)) {
            let all = message[UnknownFieldHandler.symbol];
            return fieldNo ? all.filter(uf => uf.no == fieldNo) : all;
        }
        return [];
    };
    /**
     * Returns the last unknown field by field number.
     */
    UnknownFieldHandler.last = (message, fieldNo) => UnknownFieldHandler.list(message, fieldNo).slice(-1)[0];
    const is = (message) => message && Array.isArray(message[UnknownFieldHandler.symbol]);
})(UnknownFieldHandler || (UnknownFieldHandler = {}));
/**
 * Protobuf binary format wire types.
 *
 * A wire type provides just enough information to find the length of the
 * following value.
 *
 * See https://developers.google.com/protocol-buffers/docs/encoding#structure
 */
var WireType;
(function (WireType) {
    /**
     * Used for int32, int64, uint32, uint64, sint32, sint64, bool, enum
     */
    WireType[WireType["Varint"] = 0] = "Varint";
    /**
     * Used for fixed64, sfixed64, double.
     * Always 8 bytes with little-endian byte order.
     */
    WireType[WireType["Bit64"] = 1] = "Bit64";
    /**
     * Used for string, bytes, embedded messages, packed repeated fields
     *
     * Only repeated numeric types (types which use the varint, 32-bit,
     * or 64-bit wire types) can be packed. In proto3, such fields are
     * packed by default.
     */
    WireType[WireType["LengthDelimited"] = 2] = "LengthDelimited";
    /**
     * Used for groups
     * @deprecated
     */
    WireType[WireType["StartGroup"] = 3] = "StartGroup";
    /**
     * Used for groups
     * @deprecated
     */
    WireType[WireType["EndGroup"] = 4] = "EndGroup";
    /**
     * Used for fixed32, sfixed32, float.
     * Always 4 bytes with little-endian byte order.
     */
    WireType[WireType["Bit32"] = 5] = "Bit32";
})(WireType || (WireType = {}));

// Copyright 2008 Google Inc.  All rights reserved.
//
// Redistribution and use in source and binary forms, with or without
// modification, are permitted provided that the following conditions are
// met:
//
// * Redistributions of source code must retain the above copyright
// notice, this list of conditions and the following disclaimer.
// * Redistributions in binary form must reproduce the above
// copyright notice, this list of conditions and the following disclaimer
// in the documentation and/or other materials provided with the
// distribution.
// * Neither the name of Google Inc. nor the names of its
// contributors may be used to endorse or promote products derived from
// this software without specific prior written permission.
//
// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
// "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
// LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
// A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
// OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
// SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
// LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
// DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
// THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
// OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
//
// Code generated by the Protocol Buffer compiler is owned by the owner
// of the input file used when generating it.  This code is not
// standalone and requires a support library to be linked with it.  This
// support library is itself covered by the above license.
/**
 * Read a 64 bit varint as two JS numbers.
 *
 * Returns tuple:
 * [0]: low bits
 * [0]: high bits
 *
 * Copyright 2008 Google Inc.  All rights reserved.
 *
 * See https://github.com/protocolbuffers/protobuf/blob/8a71927d74a4ce34efe2d8769fda198f52d20d12/js/experimental/runtime/kernel/buffer_decoder.js#L175
 */
function varint64read() {
    let lowBits = 0;
    let highBits = 0;
    for (let shift = 0; shift < 28; shift += 7) {
        let b = this.buf[this.pos++];
        lowBits |= (b & 0x7F) << shift;
        if ((b & 0x80) == 0) {
            this.assertBounds();
            return [lowBits, highBits];
        }
    }
    let middleByte = this.buf[this.pos++];
    // last four bits of the first 32 bit number
    lowBits |= (middleByte & 0x0F) << 28;
    // 3 upper bits are part of the next 32 bit number
    highBits = (middleByte & 0x70) >> 4;
    if ((middleByte & 0x80) == 0) {
        this.assertBounds();
        return [lowBits, highBits];
    }
    for (let shift = 3; shift <= 31; shift += 7) {
        let b = this.buf[this.pos++];
        highBits |= (b & 0x7F) << shift;
        if ((b & 0x80) == 0) {
            this.assertBounds();
            return [lowBits, highBits];
        }
    }
    throw new Error('invalid varint');
}
/**
 * Write a 64 bit varint, given as two JS numbers, to the given bytes array.
 *
 * Copyright 2008 Google Inc.  All rights reserved.
 *
 * See https://github.com/protocolbuffers/protobuf/blob/8a71927d74a4ce34efe2d8769fda198f52d20d12/js/experimental/runtime/kernel/writer.js#L344
 */
function varint64write(lo, hi, bytes) {
    for (let i = 0; i < 28; i = i + 7) {
        const shift = lo >>> i;
        const hasNext = !((shift >>> 7) == 0 && hi == 0);
        const byte = (hasNext ? shift | 0x80 : shift) & 0xFF;
        bytes.push(byte);
        if (!hasNext) {
            return;
        }
    }
    const splitBits = ((lo >>> 28) & 0x0F) | ((hi & 0x07) << 4);
    const hasMoreBits = !((hi >> 3) == 0);
    bytes.push((hasMoreBits ? splitBits | 0x80 : splitBits) & 0xFF);
    if (!hasMoreBits) {
        return;
    }
    for (let i = 3; i < 31; i = i + 7) {
        const shift = hi >>> i;
        const hasNext = !((shift >>> 7) == 0);
        const byte = (hasNext ? shift | 0x80 : shift) & 0xFF;
        bytes.push(byte);
        if (!hasNext) {
            return;
        }
    }
    bytes.push((hi >>> 31) & 0x01);
}
// constants for binary math
const TWO_PWR_32_DBL$1 = (1 << 16) * (1 << 16);
/**
 * Parse decimal string of 64 bit integer value as two JS numbers.
 *
 * Returns tuple:
 * [0]: minus sign?
 * [1]: low bits
 * [2]: high bits
 *
 * Copyright 2008 Google Inc.
 */
function int64fromString(dec) {
    // Check for minus sign.
    let minus = dec[0] == '-';
    if (minus)
        dec = dec.slice(1);
    // Work 6 decimal digits at a time, acting like we're converting base 1e6
    // digits to binary. This is safe to do with floating point math because
    // Number.isSafeInteger(ALL_32_BITS * 1e6) == true.
    const base = 1e6;
    let lowBits = 0;
    let highBits = 0;
    function add1e6digit(begin, end) {
        // Note: Number('') is 0.
        const digit1e6 = Number(dec.slice(begin, end));
        highBits *= base;
        lowBits = lowBits * base + digit1e6;
        // Carry bits from lowBits to highBits
        if (lowBits >= TWO_PWR_32_DBL$1) {
            highBits = highBits + ((lowBits / TWO_PWR_32_DBL$1) | 0);
            lowBits = lowBits % TWO_PWR_32_DBL$1;
        }
    }
    add1e6digit(-24, -18);
    add1e6digit(-18, -12);
    add1e6digit(-12, -6);
    add1e6digit(-6);
    return [minus, lowBits, highBits];
}
/**
 * Format 64 bit integer value (as two JS numbers) to decimal string.
 *
 * Copyright 2008 Google Inc.
 */
function int64toString(bitsLow, bitsHigh) {
    // Skip the expensive conversion if the number is small enough to use the
    // built-in conversions.
    if ((bitsHigh >>> 0) <= 0x1FFFFF) {
        return '' + (TWO_PWR_32_DBL$1 * bitsHigh + (bitsLow >>> 0));
    }
    // What this code is doing is essentially converting the input number from
    // base-2 to base-1e7, which allows us to represent the 64-bit range with
    // only 3 (very large) digits. Those digits are then trivial to convert to
    // a base-10 string.
    // The magic numbers used here are -
    // 2^24 = 16777216 = (1,6777216) in base-1e7.
    // 2^48 = 281474976710656 = (2,8147497,6710656) in base-1e7.
    // Split 32:32 representation into 16:24:24 representation so our
    // intermediate digits don't overflow.
    let low = bitsLow & 0xFFFFFF;
    let mid = (((bitsLow >>> 24) | (bitsHigh << 8)) >>> 0) & 0xFFFFFF;
    let high = (bitsHigh >> 16) & 0xFFFF;
    // Assemble our three base-1e7 digits, ignoring carries. The maximum
    // value in a digit at this step is representable as a 48-bit integer, which
    // can be stored in a 64-bit floating point number.
    let digitA = low + (mid * 6777216) + (high * 6710656);
    let digitB = mid + (high * 8147497);
    let digitC = (high * 2);
    // Apply carries from A to B and from B to C.
    let base = 10000000;
    if (digitA >= base) {
        digitB += Math.floor(digitA / base);
        digitA %= base;
    }
    if (digitB >= base) {
        digitC += Math.floor(digitB / base);
        digitB %= base;
    }
    // Convert base-1e7 digits to base-10, with optional leading zeroes.
    function decimalFrom1e7(digit1e7, needLeadingZeros) {
        let partial = digit1e7 ? String(digit1e7) : '';
        if (needLeadingZeros) {
            return '0000000'.slice(partial.length) + partial;
        }
        return partial;
    }
    return decimalFrom1e7(digitC, /*needLeadingZeros=*/ 0) +
        decimalFrom1e7(digitB, /*needLeadingZeros=*/ digitC) +
        // If the final 1e7 digit didn't need leading zeros, we would have
        // returned via the trivial code path at the top.
        decimalFrom1e7(digitA, /*needLeadingZeros=*/ 1);
}
/**
 * Write a 32 bit varint, signed or unsigned. Same as `varint64write(0, value, bytes)`
 *
 * Copyright 2008 Google Inc.  All rights reserved.
 *
 * See https://github.com/protocolbuffers/protobuf/blob/1b18833f4f2a2f681f4e4a25cdf3b0a43115ec26/js/binary/encoder.js#L144
 */
function varint32write(value, bytes) {
    if (value >= 0) {
        // write value as varint 32
        while (value > 0x7f) {
            bytes.push((value & 0x7f) | 0x80);
            value = value >>> 7;
        }
        bytes.push(value);
    }
    else {
        for (let i = 0; i < 9; i++) {
            bytes.push(value & 127 | 128);
            value = value >> 7;
        }
        bytes.push(1);
    }
}
/**
 * Read an unsigned 32 bit varint.
 *
 * See https://github.com/protocolbuffers/protobuf/blob/8a71927d74a4ce34efe2d8769fda198f52d20d12/js/experimental/runtime/kernel/buffer_decoder.js#L220
 */
function varint32read() {
    let b = this.buf[this.pos++];
    let result = b & 0x7F;
    if ((b & 0x80) == 0) {
        this.assertBounds();
        return result;
    }
    b = this.buf[this.pos++];
    result |= (b & 0x7F) << 7;
    if ((b & 0x80) == 0) {
        this.assertBounds();
        return result;
    }
    b = this.buf[this.pos++];
    result |= (b & 0x7F) << 14;
    if ((b & 0x80) == 0) {
        this.assertBounds();
        return result;
    }
    b = this.buf[this.pos++];
    result |= (b & 0x7F) << 21;
    if ((b & 0x80) == 0) {
        this.assertBounds();
        return result;
    }
    // Extract only last 4 bits
    b = this.buf[this.pos++];
    result |= (b & 0x0F) << 28;
    for (let readBytes = 5; ((b & 0x80) !== 0) && readBytes < 10; readBytes++)
        b = this.buf[this.pos++];
    if ((b & 0x80) != 0)
        throw new Error('invalid varint');
    this.assertBounds();
    // Result can have 32 bits, convert it to unsigned
    return result >>> 0;
}

let BI;
function detectBi() {
    const dv = new DataView(new ArrayBuffer(8));
    const ok = globalThis.BigInt !== undefined
        && typeof dv.getBigInt64 === "function"
        && typeof dv.getBigUint64 === "function"
        && typeof dv.setBigInt64 === "function"
        && typeof dv.setBigUint64 === "function";
    BI = ok ? {
        MIN: BigInt("-9223372036854775808"),
        MAX: BigInt("9223372036854775807"),
        UMIN: BigInt("0"),
        UMAX: BigInt("18446744073709551615"),
        C: BigInt,
        V: dv,
    } : undefined;
}
detectBi();
function assertBi(bi) {
    if (!bi)
        throw new Error("BigInt unavailable, see https://github.com/timostamm/protobuf-ts/blob/v1.0.8/MANUAL.md#bigint-support");
}
// used to validate from(string) input (when bigint is unavailable)
const RE_DECIMAL_STR = /^-?[0-9]+$/;
// constants for binary math
const TWO_PWR_32_DBL = 0x100000000;
const HALF_2_PWR_32 = 0x080000000;
// base class for PbLong and PbULong provides shared code
class SharedPbLong {
    /**
     * Create a new instance with the given bits.
     */
    constructor(lo, hi) {
        this.lo = lo | 0;
        this.hi = hi | 0;
    }
    /**
     * Is this instance equal to 0?
     */
    isZero() {
        return this.lo == 0 && this.hi == 0;
    }
    /**
     * Convert to a native number.
     */
    toNumber() {
        let result = this.hi * TWO_PWR_32_DBL + (this.lo >>> 0);
        if (!Number.isSafeInteger(result))
            throw new Error("cannot convert to safe number");
        return result;
    }
}
/**
 * 64-bit unsigned integer as two 32-bit values.
 * Converts between `string`, `number` and `bigint` representations.
 */
class PbULong extends SharedPbLong {
    /**
     * Create instance from a `string`, `number` or `bigint`.
     */
    static from(value) {
        if (BI)
            // noinspection FallThroughInSwitchStatementJS
            switch (typeof value) {
                case "string":
                    if (value == "0")
                        return this.ZERO;
                    if (value == "")
                        throw new Error('string is no integer');
                    value = BI.C(value);
                case "number":
                    if (value === 0)
                        return this.ZERO;
                    value = BI.C(value);
                case "bigint":
                    if (!value)
                        return this.ZERO;
                    if (value < BI.UMIN)
                        throw new Error('signed value for ulong');
                    if (value > BI.UMAX)
                        throw new Error('ulong too large');
                    BI.V.setBigUint64(0, value, true);
                    return new PbULong(BI.V.getInt32(0, true), BI.V.getInt32(4, true));
            }
        else
            switch (typeof value) {
                case "string":
                    if (value == "0")
                        return this.ZERO;
                    value = value.trim();
                    if (!RE_DECIMAL_STR.test(value))
                        throw new Error('string is no integer');
                    let [minus, lo, hi] = int64fromString(value);
                    if (minus)
                        throw new Error('signed value for ulong');
                    return new PbULong(lo, hi);
                case "number":
                    if (value == 0)
                        return this.ZERO;
                    if (!Number.isSafeInteger(value))
                        throw new Error('number is no integer');
                    if (value < 0)
                        throw new Error('signed value for ulong');
                    return new PbULong(value, value / TWO_PWR_32_DBL);
            }
        throw new Error('unknown value ' + typeof value);
    }
    /**
     * Convert to decimal string.
     */
    toString() {
        return BI ? this.toBigInt().toString() : int64toString(this.lo, this.hi);
    }
    /**
     * Convert to native bigint.
     */
    toBigInt() {
        assertBi(BI);
        BI.V.setInt32(0, this.lo, true);
        BI.V.setInt32(4, this.hi, true);
        return BI.V.getBigUint64(0, true);
    }
}
/**
 * ulong 0 singleton.
 */
PbULong.ZERO = new PbULong(0, 0);
/**
 * 64-bit signed integer as two 32-bit values.
 * Converts between `string`, `number` and `bigint` representations.
 */
class PbLong extends SharedPbLong {
    /**
     * Create instance from a `string`, `number` or `bigint`.
     */
    static from(value) {
        if (BI)
            // noinspection FallThroughInSwitchStatementJS
            switch (typeof value) {
                case "string":
                    if (value == "0")
                        return this.ZERO;
                    if (value == "")
                        throw new Error('string is no integer');
                    value = BI.C(value);
                case "number":
                    if (value === 0)
                        return this.ZERO;
                    value = BI.C(value);
                case "bigint":
                    if (!value)
                        return this.ZERO;
                    if (value < BI.MIN)
                        throw new Error('signed long too small');
                    if (value > BI.MAX)
                        throw new Error('signed long too large');
                    BI.V.setBigInt64(0, value, true);
                    return new PbLong(BI.V.getInt32(0, true), BI.V.getInt32(4, true));
            }
        else
            switch (typeof value) {
                case "string":
                    if (value == "0")
                        return this.ZERO;
                    value = value.trim();
                    if (!RE_DECIMAL_STR.test(value))
                        throw new Error('string is no integer');
                    let [minus, lo, hi] = int64fromString(value);
                    if (minus) {
                        if (hi > HALF_2_PWR_32 || (hi == HALF_2_PWR_32 && lo != 0))
                            throw new Error('signed long too small');
                    }
                    else if (hi >= HALF_2_PWR_32)
                        throw new Error('signed long too large');
                    let pbl = new PbLong(lo, hi);
                    return minus ? pbl.negate() : pbl;
                case "number":
                    if (value == 0)
                        return this.ZERO;
                    if (!Number.isSafeInteger(value))
                        throw new Error('number is no integer');
                    return value > 0
                        ? new PbLong(value, value / TWO_PWR_32_DBL)
                        : new PbLong(-value, -value / TWO_PWR_32_DBL).negate();
            }
        throw new Error('unknown value ' + typeof value);
    }
    /**
     * Do we have a minus sign?
     */
    isNegative() {
        return (this.hi & HALF_2_PWR_32) !== 0;
    }
    /**
     * Negate two's complement.
     * Invert all the bits and add one to the result.
     */
    negate() {
        let hi = ~this.hi, lo = this.lo;
        if (lo)
            lo = ~lo + 1;
        else
            hi += 1;
        return new PbLong(lo, hi);
    }
    /**
     * Convert to decimal string.
     */
    toString() {
        if (BI)
            return this.toBigInt().toString();
        if (this.isNegative()) {
            let n = this.negate();
            return '-' + int64toString(n.lo, n.hi);
        }
        return int64toString(this.lo, this.hi);
    }
    /**
     * Convert to native bigint.
     */
    toBigInt() {
        assertBi(BI);
        BI.V.setInt32(0, this.lo, true);
        BI.V.setInt32(4, this.hi, true);
        return BI.V.getBigInt64(0, true);
    }
}
/**
 * long 0 singleton.
 */
PbLong.ZERO = new PbLong(0, 0);

const defaultsRead$1 = {
    readUnknownField: true,
    readerFactory: bytes => new BinaryReader(bytes),
};
/**
 * Make options for reading binary data form partial options.
 */
function binaryReadOptions(options) {
    return options ? Object.assign(Object.assign({}, defaultsRead$1), options) : defaultsRead$1;
}
class BinaryReader {
    constructor(buf, textDecoder) {
        this.varint64 = varint64read; // dirty cast for `this`
        /**
         * Read a `uint32` field, an unsigned 32 bit varint.
         */
        this.uint32 = varint32read; // dirty cast for `this` and access to protected `buf`
        this.buf = buf;
        this.len = buf.length;
        this.pos = 0;
        this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
        this.textDecoder = textDecoder !== null && textDecoder !== void 0 ? textDecoder : new TextDecoder("utf-8", {
            fatal: true,
            ignoreBOM: true,
        });
    }
    /**
     * Reads a tag - field number and wire type.
     */
    tag() {
        let tag = this.uint32(), fieldNo = tag >>> 3, wireType = tag & 7;
        if (fieldNo <= 0 || wireType < 0 || wireType > 5)
            throw new Error("illegal tag: field no " + fieldNo + " wire type " + wireType);
        return [fieldNo, wireType];
    }
    /**
     * Skip one element on the wire and return the skipped data.
     * Supports WireType.StartGroup since v2.0.0-alpha.23.
     */
    skip(wireType) {
        let start = this.pos;
        // noinspection FallThroughInSwitchStatementJS
        switch (wireType) {
            case WireType.Varint:
                while (this.buf[this.pos++] & 0x80) {
                    // ignore
                }
                break;
            case WireType.Bit64:
                this.pos += 4;
            case WireType.Bit32:
                this.pos += 4;
                break;
            case WireType.LengthDelimited:
                let len = this.uint32();
                this.pos += len;
                break;
            case WireType.StartGroup:
                // From descriptor.proto: Group type is deprecated, not supported in proto3.
                // But we must still be able to parse and treat as unknown.
                let t;
                while ((t = this.tag()[1]) !== WireType.EndGroup) {
                    this.skip(t);
                }
                break;
            default:
                throw new Error("cant skip wire type " + wireType);
        }
        this.assertBounds();
        return this.buf.subarray(start, this.pos);
    }
    /**
     * Throws error if position in byte array is out of range.
     */
    assertBounds() {
        if (this.pos > this.len)
            throw new RangeError("premature EOF");
    }
    /**
     * Read a `int32` field, a signed 32 bit varint.
     */
    int32() {
        return this.uint32() | 0;
    }
    /**
     * Read a `sint32` field, a signed, zigzag-encoded 32-bit varint.
     */
    sint32() {
        let zze = this.uint32();
        // decode zigzag
        return (zze >>> 1) ^ -(zze & 1);
    }
    /**
     * Read a `int64` field, a signed 64-bit varint.
     */
    int64() {
        return new PbLong(...this.varint64());
    }
    /**
     * Read a `uint64` field, an unsigned 64-bit varint.
     */
    uint64() {
        return new PbULong(...this.varint64());
    }
    /**
     * Read a `sint64` field, a signed, zig-zag-encoded 64-bit varint.
     */
    sint64() {
        let [lo, hi] = this.varint64();
        // decode zig zag
        let s = -(lo & 1);
        lo = ((lo >>> 1 | (hi & 1) << 31) ^ s);
        hi = (hi >>> 1 ^ s);
        return new PbLong(lo, hi);
    }
    /**
     * Read a `bool` field, a variant.
     */
    bool() {
        let [lo, hi] = this.varint64();
        return lo !== 0 || hi !== 0;
    }
    /**
     * Read a `fixed32` field, an unsigned, fixed-length 32-bit integer.
     */
    fixed32() {
        return this.view.getUint32((this.pos += 4) - 4, true);
    }
    /**
     * Read a `sfixed32` field, a signed, fixed-length 32-bit integer.
     */
    sfixed32() {
        return this.view.getInt32((this.pos += 4) - 4, true);
    }
    /**
     * Read a `fixed64` field, an unsigned, fixed-length 64 bit integer.
     */
    fixed64() {
        return new PbULong(this.sfixed32(), this.sfixed32());
    }
    /**
     * Read a `fixed64` field, a signed, fixed-length 64-bit integer.
     */
    sfixed64() {
        return new PbLong(this.sfixed32(), this.sfixed32());
    }
    /**
     * Read a `float` field, 32-bit floating point number.
     */
    float() {
        return this.view.getFloat32((this.pos += 4) - 4, true);
    }
    /**
     * Read a `double` field, a 64-bit floating point number.
     */
    double() {
        return this.view.getFloat64((this.pos += 8) - 8, true);
    }
    /**
     * Read a `bytes` field, length-delimited arbitrary data.
     */
    bytes() {
        let len = this.uint32();
        let start = this.pos;
        this.pos += len;
        this.assertBounds();
        return this.buf.subarray(start, start + len);
    }
    /**
     * Read a `string` field, length-delimited data converted to UTF-8 text.
     */
    string() {
        return this.textDecoder.decode(this.bytes());
    }
}

/**
 * assert that condition is true or throw error (with message)
 */
function assert(condition, msg) {
    if (!condition) {
        throw new Error(msg);
    }
}
const FLOAT32_MAX = 3.4028234663852886e+38, FLOAT32_MIN = -34028234663852886e22, UINT32_MAX = 0xFFFFFFFF, INT32_MAX = 0X7FFFFFFF, INT32_MIN = -2147483648;
function assertInt32(arg) {
    if (typeof arg !== "number")
        throw new Error('invalid int 32: ' + typeof arg);
    if (!Number.isInteger(arg) || arg > INT32_MAX || arg < INT32_MIN)
        throw new Error('invalid int 32: ' + arg);
}
function assertUInt32(arg) {
    if (typeof arg !== "number")
        throw new Error('invalid uint 32: ' + typeof arg);
    if (!Number.isInteger(arg) || arg > UINT32_MAX || arg < 0)
        throw new Error('invalid uint 32: ' + arg);
}
function assertFloat32(arg) {
    if (typeof arg !== "number")
        throw new Error('invalid float 32: ' + typeof arg);
    if (!Number.isFinite(arg))
        return;
    if (arg > FLOAT32_MAX || arg < FLOAT32_MIN)
        throw new Error('invalid float 32: ' + arg);
}

const defaultsWrite$1 = {
    writeUnknownFields: true,
    writerFactory: () => new BinaryWriter(),
};
/**
 * Make options for writing binary data form partial options.
 */
function binaryWriteOptions(options) {
    return options ? Object.assign(Object.assign({}, defaultsWrite$1), options) : defaultsWrite$1;
}
class BinaryWriter {
    constructor(textEncoder) {
        /**
         * Previous fork states.
         */
        this.stack = [];
        this.textEncoder = textEncoder !== null && textEncoder !== void 0 ? textEncoder : new TextEncoder();
        this.chunks = [];
        this.buf = [];
    }
    /**
     * Return all bytes written and reset this writer.
     */
    finish() {
        this.chunks.push(new Uint8Array(this.buf)); // flush the buffer
        let len = 0;
        for (let i = 0; i < this.chunks.length; i++)
            len += this.chunks[i].length;
        let bytes = new Uint8Array(len);
        let offset = 0;
        for (let i = 0; i < this.chunks.length; i++) {
            bytes.set(this.chunks[i], offset);
            offset += this.chunks[i].length;
        }
        this.chunks = [];
        return bytes;
    }
    /**
     * Start a new fork for length-delimited data like a message
     * or a packed repeated field.
     *
     * Must be joined later with `join()`.
     */
    fork() {
        this.stack.push({ chunks: this.chunks, buf: this.buf });
        this.chunks = [];
        this.buf = [];
        return this;
    }
    /**
     * Join the last fork. Write its length and bytes, then
     * return to the previous state.
     */
    join() {
        // get chunk of fork
        let chunk = this.finish();
        // restore previous state
        let prev = this.stack.pop();
        if (!prev)
            throw new Error('invalid state, fork stack empty');
        this.chunks = prev.chunks;
        this.buf = prev.buf;
        // write length of chunk as varint
        this.uint32(chunk.byteLength);
        return this.raw(chunk);
    }
    /**
     * Writes a tag (field number and wire type).
     *
     * Equivalent to `uint32( (fieldNo << 3 | type) >>> 0 )`.
     *
     * Generated code should compute the tag ahead of time and call `uint32()`.
     */
    tag(fieldNo, type) {
        return this.uint32((fieldNo << 3 | type) >>> 0);
    }
    /**
     * Write a chunk of raw bytes.
     */
    raw(chunk) {
        if (this.buf.length) {
            this.chunks.push(new Uint8Array(this.buf));
            this.buf = [];
        }
        this.chunks.push(chunk);
        return this;
    }
    /**
     * Write a `uint32` value, an unsigned 32 bit varint.
     */
    uint32(value) {
        assertUInt32(value);
        // write value as varint 32, inlined for speed
        while (value > 0x7f) {
            this.buf.push((value & 0x7f) | 0x80);
            value = value >>> 7;
        }
        this.buf.push(value);
        return this;
    }
    /**
     * Write a `int32` value, a signed 32 bit varint.
     */
    int32(value) {
        assertInt32(value);
        varint32write(value, this.buf);
        return this;
    }
    /**
     * Write a `bool` value, a variant.
     */
    bool(value) {
        this.buf.push(value ? 1 : 0);
        return this;
    }
    /**
     * Write a `bytes` value, length-delimited arbitrary data.
     */
    bytes(value) {
        this.uint32(value.byteLength); // write length of chunk as varint
        return this.raw(value);
    }
    /**
     * Write a `string` value, length-delimited data converted to UTF-8 text.
     */
    string(value) {
        let chunk = this.textEncoder.encode(value);
        this.uint32(chunk.byteLength); // write length of chunk as varint
        return this.raw(chunk);
    }
    /**
     * Write a `float` value, 32-bit floating point number.
     */
    float(value) {
        assertFloat32(value);
        let chunk = new Uint8Array(4);
        new DataView(chunk.buffer).setFloat32(0, value, true);
        return this.raw(chunk);
    }
    /**
     * Write a `double` value, a 64-bit floating point number.
     */
    double(value) {
        let chunk = new Uint8Array(8);
        new DataView(chunk.buffer).setFloat64(0, value, true);
        return this.raw(chunk);
    }
    /**
     * Write a `fixed32` value, an unsigned, fixed-length 32-bit integer.
     */
    fixed32(value) {
        assertUInt32(value);
        let chunk = new Uint8Array(4);
        new DataView(chunk.buffer).setUint32(0, value, true);
        return this.raw(chunk);
    }
    /**
     * Write a `sfixed32` value, a signed, fixed-length 32-bit integer.
     */
    sfixed32(value) {
        assertInt32(value);
        let chunk = new Uint8Array(4);
        new DataView(chunk.buffer).setInt32(0, value, true);
        return this.raw(chunk);
    }
    /**
     * Write a `sint32` value, a signed, zigzag-encoded 32-bit varint.
     */
    sint32(value) {
        assertInt32(value);
        // zigzag encode
        value = ((value << 1) ^ (value >> 31)) >>> 0;
        varint32write(value, this.buf);
        return this;
    }
    /**
     * Write a `fixed64` value, a signed, fixed-length 64-bit integer.
     */
    sfixed64(value) {
        let chunk = new Uint8Array(8);
        let view = new DataView(chunk.buffer);
        let long = PbLong.from(value);
        view.setInt32(0, long.lo, true);
        view.setInt32(4, long.hi, true);
        return this.raw(chunk);
    }
    /**
     * Write a `fixed64` value, an unsigned, fixed-length 64 bit integer.
     */
    fixed64(value) {
        let chunk = new Uint8Array(8);
        let view = new DataView(chunk.buffer);
        let long = PbULong.from(value);
        view.setInt32(0, long.lo, true);
        view.setInt32(4, long.hi, true);
        return this.raw(chunk);
    }
    /**
     * Write a `int64` value, a signed 64-bit varint.
     */
    int64(value) {
        let long = PbLong.from(value);
        varint64write(long.lo, long.hi, this.buf);
        return this;
    }
    /**
     * Write a `sint64` value, a signed, zig-zag-encoded 64-bit varint.
     */
    sint64(value) {
        let long = PbLong.from(value), 
        // zigzag encode
        sign = long.hi >> 31, lo = (long.lo << 1) ^ sign, hi = ((long.hi << 1) | (long.lo >>> 31)) ^ sign;
        varint64write(lo, hi, this.buf);
        return this;
    }
    /**
     * Write a `uint64` value, an unsigned 64-bit varint.
     */
    uint64(value) {
        let long = PbULong.from(value);
        varint64write(long.lo, long.hi, this.buf);
        return this;
    }
}

const defaultsWrite = {
    emitDefaultValues: false,
    enumAsInteger: false,
    useProtoFieldName: false,
    prettySpaces: 0,
}, defaultsRead = {
    ignoreUnknownFields: false,
};
/**
 * Make options for reading JSON data from partial options.
 */
function jsonReadOptions(options) {
    return options ? Object.assign(Object.assign({}, defaultsRead), options) : defaultsRead;
}
/**
 * Make options for writing JSON data from partial options.
 */
function jsonWriteOptions(options) {
    return options ? Object.assign(Object.assign({}, defaultsWrite), options) : defaultsWrite;
}

/**
 * The symbol used as a key on message objects to store the message type.
 *
 * Note that this is an experimental feature - it is here to stay, but
 * implementation details may change without notice.
 */
const MESSAGE_TYPE = Symbol.for("protobuf-ts/message-type");

/**
 * Converts snake_case to lowerCamelCase.
 *
 * Should behave like protoc:
 * https://github.com/protocolbuffers/protobuf/blob/e8ae137c96444ea313485ed1118c5e43b2099cf1/src/google/protobuf/compiler/java/java_helpers.cc#L118
 */
function lowerCamelCase(snakeCase) {
    let capNext = false;
    const sb = [];
    for (let i = 0; i < snakeCase.length; i++) {
        let next = snakeCase.charAt(i);
        if (next == '_') {
            capNext = true;
        }
        else if (/\d/.test(next)) {
            sb.push(next);
            capNext = true;
        }
        else if (capNext) {
            sb.push(next.toUpperCase());
            capNext = false;
        }
        else if (i == 0) {
            sb.push(next.toLowerCase());
        }
        else {
            sb.push(next);
        }
    }
    return sb.join('');
}

/**
 * Scalar value types. This is a subset of field types declared by protobuf
 * enum google.protobuf.FieldDescriptorProto.Type The types GROUP and MESSAGE
 * are omitted, but the numerical values are identical.
 */
var ScalarType;
(function (ScalarType) {
    // 0 is reserved for errors.
    // Order is weird for historical reasons.
    ScalarType[ScalarType["DOUBLE"] = 1] = "DOUBLE";
    ScalarType[ScalarType["FLOAT"] = 2] = "FLOAT";
    // Not ZigZag encoded.  Negative numbers take 10 bytes.  Use TYPE_SINT64 if
    // negative values are likely.
    ScalarType[ScalarType["INT64"] = 3] = "INT64";
    ScalarType[ScalarType["UINT64"] = 4] = "UINT64";
    // Not ZigZag encoded.  Negative numbers take 10 bytes.  Use TYPE_SINT32 if
    // negative values are likely.
    ScalarType[ScalarType["INT32"] = 5] = "INT32";
    ScalarType[ScalarType["FIXED64"] = 6] = "FIXED64";
    ScalarType[ScalarType["FIXED32"] = 7] = "FIXED32";
    ScalarType[ScalarType["BOOL"] = 8] = "BOOL";
    ScalarType[ScalarType["STRING"] = 9] = "STRING";
    // Tag-delimited aggregate.
    // Group type is deprecated and not supported in proto3. However, Proto3
    // implementations should still be able to parse the group wire format and
    // treat group fields as unknown fields.
    // TYPE_GROUP = 10,
    // TYPE_MESSAGE = 11,  // Length-delimited aggregate.
    // New in version 2.
    ScalarType[ScalarType["BYTES"] = 12] = "BYTES";
    ScalarType[ScalarType["UINT32"] = 13] = "UINT32";
    // TYPE_ENUM = 14,
    ScalarType[ScalarType["SFIXED32"] = 15] = "SFIXED32";
    ScalarType[ScalarType["SFIXED64"] = 16] = "SFIXED64";
    ScalarType[ScalarType["SINT32"] = 17] = "SINT32";
    ScalarType[ScalarType["SINT64"] = 18] = "SINT64";
})(ScalarType || (ScalarType = {}));
/**
 * JavaScript representation of 64 bit integral types. Equivalent to the
 * field option "jstype".
 *
 * By default, protobuf-ts represents 64 bit types as `bigint`.
 *
 * You can change the default behaviour by enabling the plugin parameter
 * `long_type_string`, which will represent 64 bit types as `string`.
 *
 * Alternatively, you can change the behaviour for individual fields
 * with the field option "jstype":
 *
 * ```protobuf
 * uint64 my_field = 1 [jstype = JS_STRING];
 * uint64 other_field = 2 [jstype = JS_NUMBER];
 * ```
 */
var LongType;
(function (LongType) {
    /**
     * Use JavaScript `bigint`.
     *
     * Field option `[jstype = JS_NORMAL]`.
     */
    LongType[LongType["BIGINT"] = 0] = "BIGINT";
    /**
     * Use JavaScript `string`.
     *
     * Field option `[jstype = JS_STRING]`.
     */
    LongType[LongType["STRING"] = 1] = "STRING";
    /**
     * Use JavaScript `number`.
     *
     * Large values will loose precision.
     *
     * Field option `[jstype = JS_NUMBER]`.
     */
    LongType[LongType["NUMBER"] = 2] = "NUMBER";
})(LongType || (LongType = {}));
/**
 * Protobuf 2.1.0 introduced packed repeated fields.
 * Setting the field option `[packed = true]` enables packing.
 *
 * In proto3, all repeated fields are packed by default.
 * Setting the field option `[packed = false]` disables packing.
 *
 * Packed repeated fields are encoded with a single tag,
 * then a length-delimiter, then the element values.
 *
 * Unpacked repeated fields are encoded with a tag and
 * value for each element.
 *
 * `bytes` and `string` cannot be packed.
 */
var RepeatType;
(function (RepeatType) {
    /**
     * The field is not repeated.
     */
    RepeatType[RepeatType["NO"] = 0] = "NO";
    /**
     * The field is repeated and should be packed.
     * Invalid for `bytes` and `string`, they cannot be packed.
     */
    RepeatType[RepeatType["PACKED"] = 1] = "PACKED";
    /**
     * The field is repeated but should not be packed.
     * The only valid repeat type for repeated `bytes` and `string`.
     */
    RepeatType[RepeatType["UNPACKED"] = 2] = "UNPACKED";
})(RepeatType || (RepeatType = {}));
/**
 * Turns PartialFieldInfo into FieldInfo.
 */
function normalizeFieldInfo(field) {
    var _a, _b, _c, _d;
    field.localName = (_a = field.localName) !== null && _a !== void 0 ? _a : lowerCamelCase(field.name);
    field.jsonName = (_b = field.jsonName) !== null && _b !== void 0 ? _b : lowerCamelCase(field.name);
    field.repeat = (_c = field.repeat) !== null && _c !== void 0 ? _c : RepeatType.NO;
    field.opt = (_d = field.opt) !== null && _d !== void 0 ? _d : (field.repeat ? false : field.oneof ? false : field.kind == "message");
    return field;
}

/**
 * Is the given value a valid oneof group?
 *
 * We represent protobuf `oneof` as algebraic data types (ADT) in generated
 * code. But when working with messages of unknown type, the ADT does not
 * help us.
 *
 * This type guard checks if the given object adheres to the ADT rules, which
 * are as follows:
 *
 * 1) Must be an object.
 *
 * 2) Must have a "oneofKind" discriminator property.
 *
 * 3) If "oneofKind" is `undefined`, no member field is selected. The object
 * must not have any other properties.
 *
 * 4) If "oneofKind" is a `string`, the member field with this name is
 * selected.
 *
 * 5) If a member field is selected, the object must have a second property
 * with this name. The property must not be `undefined`.
 *
 * 6) No extra properties are allowed. The object has either one property
 * (no selection) or two properties (selection).
 *
 */
function isOneofGroup(any) {
    if (typeof any != 'object' || any === null || !any.hasOwnProperty('oneofKind')) {
        return false;
    }
    switch (typeof any.oneofKind) {
        case "string":
            if (any[any.oneofKind] === undefined)
                return false;
            return Object.keys(any).length == 2;
        case "undefined":
            return Object.keys(any).length == 1;
        default:
            return false;
    }
}

// noinspection JSMethodCanBeStatic
class ReflectionTypeCheck {
    constructor(info) {
        var _a;
        this.fields = (_a = info.fields) !== null && _a !== void 0 ? _a : [];
    }
    prepare() {
        if (this.data)
            return;
        const req = [], known = [], oneofs = [];
        for (let field of this.fields) {
            if (field.oneof) {
                if (!oneofs.includes(field.oneof)) {
                    oneofs.push(field.oneof);
                    req.push(field.oneof);
                    known.push(field.oneof);
                }
            }
            else {
                known.push(field.localName);
                switch (field.kind) {
                    case "scalar":
                    case "enum":
                        if (!field.opt || field.repeat)
                            req.push(field.localName);
                        break;
                    case "message":
                        if (field.repeat)
                            req.push(field.localName);
                        break;
                    case "map":
                        req.push(field.localName);
                        break;
                }
            }
        }
        this.data = { req, known, oneofs: Object.values(oneofs) };
    }
    /**
     * Is the argument a valid message as specified by the
     * reflection information?
     *
     * Checks all field types recursively. The `depth`
     * specifies how deep into the structure the check will be.
     *
     * With a depth of 0, only the presence of fields
     * is checked.
     *
     * With a depth of 1 or more, the field types are checked.
     *
     * With a depth of 2 or more, the members of map, repeated
     * and message fields are checked.
     *
     * Message fields will be checked recursively with depth - 1.
     *
     * The number of map entries / repeated values being checked
     * is < depth.
     */
    is(message, depth, allowExcessProperties = false) {
        if (depth < 0)
            return true;
        if (message === null || message === undefined || typeof message != 'object')
            return false;
        this.prepare();
        let keys = Object.keys(message), data = this.data;
        // if a required field is missing in arg, this cannot be a T
        if (keys.length < data.req.length || data.req.some(n => !keys.includes(n)))
            return false;
        if (!allowExcessProperties) {
            // if the arg contains a key we dont know, this is not a literal T
            if (keys.some(k => !data.known.includes(k)))
                return false;
        }
        // "With a depth of 0, only the presence and absence of fields is checked."
        // "With a depth of 1 or more, the field types are checked."
        if (depth < 1) {
            return true;
        }
        // check oneof group
        for (const name of data.oneofs) {
            const group = message[name];
            if (!isOneofGroup(group))
                return false;
            if (group.oneofKind === undefined)
                continue;
            const field = this.fields.find(f => f.localName === group.oneofKind);
            if (!field)
                return false; // we found no field, but have a kind, something is wrong
            if (!this.field(group[group.oneofKind], field, allowExcessProperties, depth))
                return false;
        }
        // check types
        for (const field of this.fields) {
            if (field.oneof !== undefined)
                continue;
            if (!this.field(message[field.localName], field, allowExcessProperties, depth))
                return false;
        }
        return true;
    }
    field(arg, field, allowExcessProperties, depth) {
        let repeated = field.repeat;
        switch (field.kind) {
            case "scalar":
                if (arg === undefined)
                    return field.opt;
                if (repeated)
                    return this.scalars(arg, field.T, depth, field.L);
                return this.scalar(arg, field.T, field.L);
            case "enum":
                if (arg === undefined)
                    return field.opt;
                if (repeated)
                    return this.scalars(arg, ScalarType.INT32, depth);
                return this.scalar(arg, ScalarType.INT32);
            case "message":
                if (arg === undefined)
                    return true;
                if (repeated)
                    return this.messages(arg, field.T(), allowExcessProperties, depth);
                return this.message(arg, field.T(), allowExcessProperties, depth);
            case "map":
                if (typeof arg != 'object' || arg === null)
                    return false;
                if (depth < 2)
                    return true;
                if (!this.mapKeys(arg, field.K, depth))
                    return false;
                switch (field.V.kind) {
                    case "scalar":
                        return this.scalars(Object.values(arg), field.V.T, depth, field.V.L);
                    case "enum":
                        return this.scalars(Object.values(arg), ScalarType.INT32, depth);
                    case "message":
                        return this.messages(Object.values(arg), field.V.T(), allowExcessProperties, depth);
                }
                break;
        }
        return true;
    }
    message(arg, type, allowExcessProperties, depth) {
        if (allowExcessProperties) {
            return type.isAssignable(arg, depth);
        }
        return type.is(arg, depth);
    }
    messages(arg, type, allowExcessProperties, depth) {
        if (!Array.isArray(arg))
            return false;
        if (depth < 2)
            return true;
        if (allowExcessProperties) {
            for (let i = 0; i < arg.length && i < depth; i++)
                if (!type.isAssignable(arg[i], depth - 1))
                    return false;
        }
        else {
            for (let i = 0; i < arg.length && i < depth; i++)
                if (!type.is(arg[i], depth - 1))
                    return false;
        }
        return true;
    }
    scalar(arg, type, longType) {
        let argType = typeof arg;
        switch (type) {
            case ScalarType.UINT64:
            case ScalarType.FIXED64:
            case ScalarType.INT64:
            case ScalarType.SFIXED64:
            case ScalarType.SINT64:
                switch (longType) {
                    case LongType.BIGINT:
                        return argType == "bigint";
                    case LongType.NUMBER:
                        return argType == "number" && !isNaN(arg);
                    default:
                        return argType == "string";
                }
            case ScalarType.BOOL:
                return argType == 'boolean';
            case ScalarType.STRING:
                return argType == 'string';
            case ScalarType.BYTES:
                return arg instanceof Uint8Array;
            case ScalarType.DOUBLE:
            case ScalarType.FLOAT:
                return argType == 'number' && !isNaN(arg);
            default:
                // case ScalarType.UINT32:
                // case ScalarType.FIXED32:
                // case ScalarType.INT32:
                // case ScalarType.SINT32:
                // case ScalarType.SFIXED32:
                return argType == 'number' && Number.isInteger(arg);
        }
    }
    scalars(arg, type, depth, longType) {
        if (!Array.isArray(arg))
            return false;
        if (depth < 2)
            return true;
        if (Array.isArray(arg))
            for (let i = 0; i < arg.length && i < depth; i++)
                if (!this.scalar(arg[i], type, longType))
                    return false;
        return true;
    }
    mapKeys(map, type, depth) {
        let keys = Object.keys(map);
        switch (type) {
            case ScalarType.INT32:
            case ScalarType.FIXED32:
            case ScalarType.SFIXED32:
            case ScalarType.SINT32:
            case ScalarType.UINT32:
                return this.scalars(keys.slice(0, depth).map(k => parseInt(k)), type, depth);
            case ScalarType.BOOL:
                return this.scalars(keys.slice(0, depth).map(k => k == 'true' ? true : k == 'false' ? false : k), type, depth);
            default:
                return this.scalars(keys, type, depth, LongType.STRING);
        }
    }
}

/**
 * Utility method to convert a PbLong or PbUlong to a JavaScript
 * representation during runtime.
 *
 * Works with generated field information, `undefined` is equivalent
 * to `STRING`.
 */
function reflectionLongConvert(long, type) {
    switch (type) {
        case LongType.BIGINT:
            return long.toBigInt();
        case LongType.NUMBER:
            return long.toNumber();
        default:
            // case undefined:
            // case LongType.STRING:
            return long.toString();
    }
}

/**
 * Reads proto3 messages in canonical JSON format using reflection information.
 *
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 */
class ReflectionJsonReader {
    constructor(info) {
        this.info = info;
    }
    prepare() {
        var _a;
        if (this.fMap === undefined) {
            this.fMap = {};
            const fieldsInput = (_a = this.info.fields) !== null && _a !== void 0 ? _a : [];
            for (const field of fieldsInput) {
                this.fMap[field.name] = field;
                this.fMap[field.jsonName] = field;
                this.fMap[field.localName] = field;
            }
        }
    }
    // Cannot parse JSON <type of jsonValue> for <type name>#<fieldName>.
    assert(condition, fieldName, jsonValue) {
        if (!condition) {
            let what = typeofJsonValue(jsonValue);
            if (what == "number" || what == "boolean")
                what = jsonValue.toString();
            throw new Error(`Cannot parse JSON ${what} for ${this.info.typeName}#${fieldName}`);
        }
    }
    /**
     * Reads a message from canonical JSON format into the target message.
     *
     * Repeated fields are appended. Map entries are added, overwriting
     * existing keys.
     *
     * If a message field is already present, it will be merged with the
     * new data.
     */
    read(input, message, options) {
        this.prepare();
        const oneofsHandled = [];
        for (const [jsonKey, jsonValue] of Object.entries(input)) {
            const field = this.fMap[jsonKey];
            if (!field) {
                if (!options.ignoreUnknownFields)
                    throw new Error(`Found unknown field while reading ${this.info.typeName} from JSON format. JSON key: ${jsonKey}`);
                continue;
            }
            const localName = field.localName;
            // handle oneof ADT
            let target; // this will be the target for the field value, whether it is member of a oneof or not
            if (field.oneof) {
                if (jsonValue === null && (field.kind !== 'enum' || field.T()[0] !== 'google.protobuf.NullValue')) {
                    continue;
                }
                // since json objects are unordered by specification, it is not possible to take the last of multiple oneofs
                if (oneofsHandled.includes(field.oneof))
                    throw new Error(`Multiple members of the oneof group "${field.oneof}" of ${this.info.typeName} are present in JSON.`);
                oneofsHandled.push(field.oneof);
                target = message[field.oneof] = {
                    oneofKind: localName
                };
            }
            else {
                target = message;
            }
            // we have handled oneof above. we just have read the value into `target`.
            if (field.kind == 'map') {
                if (jsonValue === null) {
                    continue;
                }
                // check input
                this.assert(isJsonObject(jsonValue), field.name, jsonValue);
                // our target to put map entries into
                const fieldObj = target[localName];
                // read entries
                for (const [jsonObjKey, jsonObjValue] of Object.entries(jsonValue)) {
                    this.assert(jsonObjValue !== null, field.name + " map value", null);
                    // read value
                    let val;
                    switch (field.V.kind) {
                        case "message":
                            val = field.V.T().internalJsonRead(jsonObjValue, options);
                            break;
                        case "enum":
                            val = this.enum(field.V.T(), jsonObjValue, field.name, options.ignoreUnknownFields);
                            if (val === false)
                                continue;
                            break;
                        case "scalar":
                            val = this.scalar(jsonObjValue, field.V.T, field.V.L, field.name);
                            break;
                    }
                    this.assert(val !== undefined, field.name + " map value", jsonObjValue);
                    // read key
                    let key = jsonObjKey;
                    if (field.K == ScalarType.BOOL)
                        key = key == "true" ? true : key == "false" ? false : key;
                    key = this.scalar(key, field.K, LongType.STRING, field.name).toString();
                    fieldObj[key] = val;
                }
            }
            else if (field.repeat) {
                if (jsonValue === null)
                    continue;
                // check input
                this.assert(Array.isArray(jsonValue), field.name, jsonValue);
                // our target to put array entries into
                const fieldArr = target[localName];
                // read array entries
                for (const jsonItem of jsonValue) {
                    this.assert(jsonItem !== null, field.name, null);
                    let val;
                    switch (field.kind) {
                        case "message":
                            val = field.T().internalJsonRead(jsonItem, options);
                            break;
                        case "enum":
                            val = this.enum(field.T(), jsonItem, field.name, options.ignoreUnknownFields);
                            if (val === false)
                                continue;
                            break;
                        case "scalar":
                            val = this.scalar(jsonItem, field.T, field.L, field.name);
                            break;
                    }
                    this.assert(val !== undefined, field.name, jsonValue);
                    fieldArr.push(val);
                }
            }
            else {
                switch (field.kind) {
                    case "message":
                        if (jsonValue === null && field.T().typeName != 'google.protobuf.Value') {
                            this.assert(field.oneof === undefined, field.name + " (oneof member)", null);
                            continue;
                        }
                        target[localName] = field.T().internalJsonRead(jsonValue, options, target[localName]);
                        break;
                    case "enum":
                        if (jsonValue === null)
                            continue;
                        let val = this.enum(field.T(), jsonValue, field.name, options.ignoreUnknownFields);
                        if (val === false)
                            continue;
                        target[localName] = val;
                        break;
                    case "scalar":
                        if (jsonValue === null)
                            continue;
                        target[localName] = this.scalar(jsonValue, field.T, field.L, field.name);
                        break;
                }
            }
        }
    }
    /**
     * Returns `false` for unrecognized string representations.
     *
     * google.protobuf.NullValue accepts only JSON `null` (or the old `"NULL_VALUE"`).
     */
    enum(type, json, fieldName, ignoreUnknownFields) {
        if (type[0] == 'google.protobuf.NullValue')
            assert(json === null || json === "NULL_VALUE", `Unable to parse field ${this.info.typeName}#${fieldName}, enum ${type[0]} only accepts null.`);
        if (json === null)
            // we require 0 to be default value for all enums
            return 0;
        switch (typeof json) {
            case "number":
                assert(Number.isInteger(json), `Unable to parse field ${this.info.typeName}#${fieldName}, enum can only be integral number, got ${json}.`);
                return json;
            case "string":
                let localEnumName = json;
                if (type[2] && json.substring(0, type[2].length) === type[2])
                    // lookup without the shared prefix
                    localEnumName = json.substring(type[2].length);
                let enumNumber = type[1][localEnumName];
                if (typeof enumNumber === 'undefined' && ignoreUnknownFields) {
                    return false;
                }
                assert(typeof enumNumber == "number", `Unable to parse field ${this.info.typeName}#${fieldName}, enum ${type[0]} has no value for "${json}".`);
                return enumNumber;
        }
        assert(false, `Unable to parse field ${this.info.typeName}#${fieldName}, cannot parse enum value from ${typeof json}".`);
    }
    scalar(json, type, longType, fieldName) {
        let e;
        try {
            switch (type) {
                // float, double: JSON value will be a number or one of the special string values "NaN", "Infinity", and "-Infinity".
                // Either numbers or strings are accepted. Exponent notation is also accepted.
                case ScalarType.DOUBLE:
                case ScalarType.FLOAT:
                    if (json === null)
                        return .0;
                    if (json === "NaN")
                        return Number.NaN;
                    if (json === "Infinity")
                        return Number.POSITIVE_INFINITY;
                    if (json === "-Infinity")
                        return Number.NEGATIVE_INFINITY;
                    if (json === "") {
                        e = "empty string";
                        break;
                    }
                    if (typeof json == "string" && json.trim().length !== json.length) {
                        e = "extra whitespace";
                        break;
                    }
                    if (typeof json != "string" && typeof json != "number") {
                        break;
                    }
                    let float = Number(json);
                    if (Number.isNaN(float)) {
                        e = "not a number";
                        break;
                    }
                    if (!Number.isFinite(float)) {
                        // infinity and -infinity are handled by string representation above, so this is an error
                        e = "too large or small";
                        break;
                    }
                    if (type == ScalarType.FLOAT)
                        assertFloat32(float);
                    return float;
                // int32, fixed32, uint32: JSON value will be a decimal number. Either numbers or strings are accepted.
                case ScalarType.INT32:
                case ScalarType.FIXED32:
                case ScalarType.SFIXED32:
                case ScalarType.SINT32:
                case ScalarType.UINT32:
                    if (json === null)
                        return 0;
                    let int32;
                    if (typeof json == "number")
                        int32 = json;
                    else if (json === "")
                        e = "empty string";
                    else if (typeof json == "string") {
                        if (json.trim().length !== json.length)
                            e = "extra whitespace";
                        else
                            int32 = Number(json);
                    }
                    if (int32 === undefined)
                        break;
                    if (type == ScalarType.UINT32)
                        assertUInt32(int32);
                    else
                        assertInt32(int32);
                    return int32;
                // int64, fixed64, uint64: JSON value will be a decimal string. Either numbers or strings are accepted.
                case ScalarType.INT64:
                case ScalarType.SFIXED64:
                case ScalarType.SINT64:
                    if (json === null)
                        return reflectionLongConvert(PbLong.ZERO, longType);
                    if (typeof json != "number" && typeof json != "string")
                        break;
                    return reflectionLongConvert(PbLong.from(json), longType);
                case ScalarType.FIXED64:
                case ScalarType.UINT64:
                    if (json === null)
                        return reflectionLongConvert(PbULong.ZERO, longType);
                    if (typeof json != "number" && typeof json != "string")
                        break;
                    return reflectionLongConvert(PbULong.from(json), longType);
                // bool:
                case ScalarType.BOOL:
                    if (json === null)
                        return false;
                    if (typeof json !== "boolean")
                        break;
                    return json;
                // string:
                case ScalarType.STRING:
                    if (json === null)
                        return "";
                    if (typeof json !== "string") {
                        e = "extra whitespace";
                        break;
                    }
                    try {
                        encodeURIComponent(json);
                    }
                    catch (e) {
                        e = "invalid UTF8";
                        break;
                    }
                    return json;
                // bytes: JSON value will be the data encoded as a string using standard base64 encoding with paddings.
                // Either standard or URL-safe base64 encoding with/without paddings are accepted.
                case ScalarType.BYTES:
                    if (json === null || json === "")
                        return new Uint8Array(0);
                    if (typeof json !== 'string')
                        break;
                    return base64decode(json);
            }
        }
        catch (error) {
            e = error.message;
        }
        this.assert(false, fieldName + (e ? " - " + e : ""), json);
    }
}

/**
 * Writes proto3 messages in canonical JSON format using reflection
 * information.
 *
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 */
class ReflectionJsonWriter {
    constructor(info) {
        var _a;
        this.fields = (_a = info.fields) !== null && _a !== void 0 ? _a : [];
    }
    /**
     * Converts the message to a JSON object, based on the field descriptors.
     */
    write(message, options) {
        const json = {}, source = message;
        for (const field of this.fields) {
            // field is not part of a oneof, simply write as is
            if (!field.oneof) {
                let jsonValue = this.field(field, source[field.localName], options);
                if (jsonValue !== undefined)
                    json[options.useProtoFieldName ? field.name : field.jsonName] = jsonValue;
                continue;
            }
            // field is part of a oneof
            const group = source[field.oneof];
            if (group.oneofKind !== field.localName)
                continue; // not selected, skip
            const opt = field.kind == 'scalar' || field.kind == 'enum'
                ? Object.assign(Object.assign({}, options), { emitDefaultValues: true }) : options;
            let jsonValue = this.field(field, group[field.localName], opt);
            assert(jsonValue !== undefined);
            json[options.useProtoFieldName ? field.name : field.jsonName] = jsonValue;
        }
        return json;
    }
    field(field, value, options) {
        let jsonValue = undefined;
        if (field.kind == 'map') {
            assert(typeof value == "object" && value !== null);
            const jsonObj = {};
            switch (field.V.kind) {
                case "scalar":
                    for (const [entryKey, entryValue] of Object.entries(value)) {
                        const val = this.scalar(field.V.T, entryValue, field.name, false, true);
                        assert(val !== undefined);
                        jsonObj[entryKey.toString()] = val; // JSON standard allows only (double quoted) string as property key
                    }
                    break;
                case "message":
                    const messageType = field.V.T();
                    for (const [entryKey, entryValue] of Object.entries(value)) {
                        const val = this.message(messageType, entryValue, field.name, options);
                        assert(val !== undefined);
                        jsonObj[entryKey.toString()] = val; // JSON standard allows only (double quoted) string as property key
                    }
                    break;
                case "enum":
                    const enumInfo = field.V.T();
                    for (const [entryKey, entryValue] of Object.entries(value)) {
                        assert(entryValue === undefined || typeof entryValue == 'number');
                        const val = this.enum(enumInfo, entryValue, field.name, false, true, options.enumAsInteger);
                        assert(val !== undefined);
                        jsonObj[entryKey.toString()] = val; // JSON standard allows only (double quoted) string as property key
                    }
                    break;
            }
            if (options.emitDefaultValues || Object.keys(jsonObj).length > 0)
                jsonValue = jsonObj;
        }
        else if (field.repeat) {
            assert(Array.isArray(value));
            const jsonArr = [];
            switch (field.kind) {
                case "scalar":
                    for (let i = 0; i < value.length; i++) {
                        const val = this.scalar(field.T, value[i], field.name, field.opt, true);
                        assert(val !== undefined);
                        jsonArr.push(val);
                    }
                    break;
                case "enum":
                    const enumInfo = field.T();
                    for (let i = 0; i < value.length; i++) {
                        assert(value[i] === undefined || typeof value[i] == 'number');
                        const val = this.enum(enumInfo, value[i], field.name, field.opt, true, options.enumAsInteger);
                        assert(val !== undefined);
                        jsonArr.push(val);
                    }
                    break;
                case "message":
                    const messageType = field.T();
                    for (let i = 0; i < value.length; i++) {
                        const val = this.message(messageType, value[i], field.name, options);
                        assert(val !== undefined);
                        jsonArr.push(val);
                    }
                    break;
            }
            // add converted array to json output
            if (options.emitDefaultValues || jsonArr.length > 0 || options.emitDefaultValues)
                jsonValue = jsonArr;
        }
        else {
            switch (field.kind) {
                case "scalar":
                    jsonValue = this.scalar(field.T, value, field.name, field.opt, options.emitDefaultValues);
                    break;
                case "enum":
                    jsonValue = this.enum(field.T(), value, field.name, field.opt, options.emitDefaultValues, options.enumAsInteger);
                    break;
                case "message":
                    jsonValue = this.message(field.T(), value, field.name, options);
                    break;
            }
        }
        return jsonValue;
    }
    /**
     * Returns `null` as the default for google.protobuf.NullValue.
     */
    enum(type, value, fieldName, optional, emitDefaultValues, enumAsInteger) {
        if (type[0] == 'google.protobuf.NullValue')
            return !emitDefaultValues && !optional ? undefined : null;
        if (value === undefined) {
            assert(optional);
            return undefined;
        }
        if (value === 0 && !emitDefaultValues && !optional)
            // we require 0 to be default value for all enums
            return undefined;
        assert(typeof value == 'number');
        assert(Number.isInteger(value));
        if (enumAsInteger || !type[1].hasOwnProperty(value))
            // if we don't now the enum value, just return the number
            return value;
        if (type[2])
            // restore the dropped prefix
            return type[2] + type[1][value];
        return type[1][value];
    }
    message(type, value, fieldName, options) {
        if (value === undefined)
            return options.emitDefaultValues ? null : undefined;
        return type.internalJsonWrite(value, options);
    }
    scalar(type, value, fieldName, optional, emitDefaultValues) {
        if (value === undefined) {
            assert(optional);
            return undefined;
        }
        const ed = emitDefaultValues || optional;
        // noinspection FallThroughInSwitchStatementJS
        switch (type) {
            // int32, fixed32, uint32: JSON value will be a decimal number. Either numbers or strings are accepted.
            case ScalarType.INT32:
            case ScalarType.SFIXED32:
            case ScalarType.SINT32:
                if (value === 0)
                    return ed ? 0 : undefined;
                assertInt32(value);
                return value;
            case ScalarType.FIXED32:
            case ScalarType.UINT32:
                if (value === 0)
                    return ed ? 0 : undefined;
                assertUInt32(value);
                return value;
            // float, double: JSON value will be a number or one of the special string values "NaN", "Infinity", and "-Infinity".
            // Either numbers or strings are accepted. Exponent notation is also accepted.
            case ScalarType.FLOAT:
                assertFloat32(value);
            case ScalarType.DOUBLE:
                if (value === 0)
                    return ed ? 0 : undefined;
                assert(typeof value == 'number');
                if (Number.isNaN(value))
                    return 'NaN';
                if (value === Number.POSITIVE_INFINITY)
                    return 'Infinity';
                if (value === Number.NEGATIVE_INFINITY)
                    return '-Infinity';
                return value;
            // string:
            case ScalarType.STRING:
                if (value === "")
                    return ed ? '' : undefined;
                assert(typeof value == 'string');
                return value;
            // bool:
            case ScalarType.BOOL:
                if (value === false)
                    return ed ? false : undefined;
                assert(typeof value == 'boolean');
                return value;
            // JSON value will be a decimal string. Either numbers or strings are accepted.
            case ScalarType.UINT64:
            case ScalarType.FIXED64:
                assert(typeof value == 'number' || typeof value == 'string' || typeof value == 'bigint');
                let ulong = PbULong.from(value);
                if (ulong.isZero() && !ed)
                    return undefined;
                return ulong.toString();
            // JSON value will be a decimal string. Either numbers or strings are accepted.
            case ScalarType.INT64:
            case ScalarType.SFIXED64:
            case ScalarType.SINT64:
                assert(typeof value == 'number' || typeof value == 'string' || typeof value == 'bigint');
                let long = PbLong.from(value);
                if (long.isZero() && !ed)
                    return undefined;
                return long.toString();
            // bytes: JSON value will be the data encoded as a string using standard base64 encoding with paddings.
            // Either standard or URL-safe base64 encoding with/without paddings are accepted.
            case ScalarType.BYTES:
                assert(value instanceof Uint8Array);
                if (!value.byteLength)
                    return ed ? "" : undefined;
                return base64encode(value);
        }
    }
}

/**
 * Creates the default value for a scalar type.
 */
function reflectionScalarDefault(type, longType = LongType.STRING) {
    switch (type) {
        case ScalarType.BOOL:
            return false;
        case ScalarType.UINT64:
        case ScalarType.FIXED64:
            return reflectionLongConvert(PbULong.ZERO, longType);
        case ScalarType.INT64:
        case ScalarType.SFIXED64:
        case ScalarType.SINT64:
            return reflectionLongConvert(PbLong.ZERO, longType);
        case ScalarType.DOUBLE:
        case ScalarType.FLOAT:
            return 0.0;
        case ScalarType.BYTES:
            return new Uint8Array(0);
        case ScalarType.STRING:
            return "";
        default:
            // case ScalarType.INT32:
            // case ScalarType.UINT32:
            // case ScalarType.SINT32:
            // case ScalarType.FIXED32:
            // case ScalarType.SFIXED32:
            return 0;
    }
}

/**
 * Reads proto3 messages in binary format using reflection information.
 *
 * https://developers.google.com/protocol-buffers/docs/encoding
 */
class ReflectionBinaryReader {
    constructor(info) {
        this.info = info;
    }
    prepare() {
        var _a;
        if (!this.fieldNoToField) {
            const fieldsInput = (_a = this.info.fields) !== null && _a !== void 0 ? _a : [];
            this.fieldNoToField = new Map(fieldsInput.map(field => [field.no, field]));
        }
    }
    /**
     * Reads a message from binary format into the target message.
     *
     * Repeated fields are appended. Map entries are added, overwriting
     * existing keys.
     *
     * If a message field is already present, it will be merged with the
     * new data.
     */
    read(reader, message, options, length) {
        this.prepare();
        const end = length === undefined ? reader.len : reader.pos + length;
        while (reader.pos < end) {
            // read the tag and find the field
            const [fieldNo, wireType] = reader.tag(), field = this.fieldNoToField.get(fieldNo);
            if (!field) {
                let u = options.readUnknownField;
                if (u == "throw")
                    throw new Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.info.typeName}`);
                let d = reader.skip(wireType);
                if (u !== false)
                    (u === true ? UnknownFieldHandler.onRead : u)(this.info.typeName, message, fieldNo, wireType, d);
                continue;
            }
            // target object for the field we are reading
            let target = message, repeated = field.repeat, localName = field.localName;
            // if field is member of oneof ADT, use ADT as target
            if (field.oneof) {
                target = target[field.oneof];
                // if other oneof member selected, set new ADT
                if (target.oneofKind !== localName)
                    target = message[field.oneof] = {
                        oneofKind: localName
                    };
            }
            // we have handled oneof above, we just have read the value into `target[localName]`
            switch (field.kind) {
                case "scalar":
                case "enum":
                    let T = field.kind == "enum" ? ScalarType.INT32 : field.T;
                    let L = field.kind == "scalar" ? field.L : undefined;
                    if (repeated) {
                        let arr = target[localName]; // safe to assume presence of array, oneof cannot contain repeated values
                        if (wireType == WireType.LengthDelimited && T != ScalarType.STRING && T != ScalarType.BYTES) {
                            let e = reader.uint32() + reader.pos;
                            while (reader.pos < e)
                                arr.push(this.scalar(reader, T, L));
                        }
                        else
                            arr.push(this.scalar(reader, T, L));
                    }
                    else
                        target[localName] = this.scalar(reader, T, L);
                    break;
                case "message":
                    if (repeated) {
                        let arr = target[localName]; // safe to assume presence of array, oneof cannot contain repeated values
                        let msg = field.T().internalBinaryRead(reader, reader.uint32(), options);
                        arr.push(msg);
                    }
                    else
                        target[localName] = field.T().internalBinaryRead(reader, reader.uint32(), options, target[localName]);
                    break;
                case "map":
                    let [mapKey, mapVal] = this.mapEntry(field, reader, options);
                    // safe to assume presence of map object, oneof cannot contain repeated values
                    target[localName][mapKey] = mapVal;
                    break;
            }
        }
    }
    /**
     * Read a map field, expecting key field = 1, value field = 2
     */
    mapEntry(field, reader, options) {
        let length = reader.uint32();
        let end = reader.pos + length;
        let key = undefined; // javascript only allows number or string for object properties
        let val = undefined;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case 1:
                    if (field.K == ScalarType.BOOL)
                        key = reader.bool().toString();
                    else
                        // long types are read as string, number types are okay as number
                        key = this.scalar(reader, field.K, LongType.STRING);
                    break;
                case 2:
                    switch (field.V.kind) {
                        case "scalar":
                            val = this.scalar(reader, field.V.T, field.V.L);
                            break;
                        case "enum":
                            val = reader.int32();
                            break;
                        case "message":
                            val = field.V.T().internalBinaryRead(reader, reader.uint32(), options);
                            break;
                    }
                    break;
                default:
                    throw new Error(`Unknown field ${fieldNo} (wire type ${wireType}) in map entry for ${this.info.typeName}#${field.name}`);
            }
        }
        if (key === undefined) {
            let keyRaw = reflectionScalarDefault(field.K);
            key = field.K == ScalarType.BOOL ? keyRaw.toString() : keyRaw;
        }
        if (val === undefined)
            switch (field.V.kind) {
                case "scalar":
                    val = reflectionScalarDefault(field.V.T, field.V.L);
                    break;
                case "enum":
                    val = 0;
                    break;
                case "message":
                    val = field.V.T().create();
                    break;
            }
        return [key, val];
    }
    scalar(reader, type, longType) {
        switch (type) {
            case ScalarType.INT32:
                return reader.int32();
            case ScalarType.STRING:
                return reader.string();
            case ScalarType.BOOL:
                return reader.bool();
            case ScalarType.DOUBLE:
                return reader.double();
            case ScalarType.FLOAT:
                return reader.float();
            case ScalarType.INT64:
                return reflectionLongConvert(reader.int64(), longType);
            case ScalarType.UINT64:
                return reflectionLongConvert(reader.uint64(), longType);
            case ScalarType.FIXED64:
                return reflectionLongConvert(reader.fixed64(), longType);
            case ScalarType.FIXED32:
                return reader.fixed32();
            case ScalarType.BYTES:
                return reader.bytes();
            case ScalarType.UINT32:
                return reader.uint32();
            case ScalarType.SFIXED32:
                return reader.sfixed32();
            case ScalarType.SFIXED64:
                return reflectionLongConvert(reader.sfixed64(), longType);
            case ScalarType.SINT32:
                return reader.sint32();
            case ScalarType.SINT64:
                return reflectionLongConvert(reader.sint64(), longType);
        }
    }
}

/**
 * Writes proto3 messages in binary format using reflection information.
 *
 * https://developers.google.com/protocol-buffers/docs/encoding
 */
class ReflectionBinaryWriter {
    constructor(info) {
        this.info = info;
    }
    prepare() {
        if (!this.fields) {
            const fieldsInput = this.info.fields ? this.info.fields.concat() : [];
            this.fields = fieldsInput.sort((a, b) => a.no - b.no);
        }
    }
    /**
     * Writes the message to binary format.
     */
    write(message, writer, options) {
        this.prepare();
        for (const field of this.fields) {
            let value, // this will be our field value, whether it is member of a oneof or not
            emitDefault, // whether we emit the default value (only true for oneof members)
            repeated = field.repeat, localName = field.localName;
            // handle oneof ADT
            if (field.oneof) {
                const group = message[field.oneof];
                if (group.oneofKind !== localName)
                    continue; // if field is not selected, skip
                value = group[localName];
                emitDefault = true;
            }
            else {
                value = message[localName];
                emitDefault = false;
            }
            // we have handled oneof above. we just have to honor `emitDefault`.
            switch (field.kind) {
                case "scalar":
                case "enum":
                    let T = field.kind == "enum" ? ScalarType.INT32 : field.T;
                    if (repeated) {
                        assert(Array.isArray(value));
                        if (repeated == RepeatType.PACKED)
                            this.packed(writer, T, field.no, value);
                        else
                            for (const item of value)
                                this.scalar(writer, T, field.no, item, true);
                    }
                    else if (value === undefined)
                        assert(field.opt);
                    else
                        this.scalar(writer, T, field.no, value, emitDefault || field.opt);
                    break;
                case "message":
                    if (repeated) {
                        assert(Array.isArray(value));
                        for (const item of value)
                            this.message(writer, options, field.T(), field.no, item);
                    }
                    else {
                        this.message(writer, options, field.T(), field.no, value);
                    }
                    break;
                case "map":
                    assert(typeof value == 'object' && value !== null);
                    for (const [key, val] of Object.entries(value))
                        this.mapEntry(writer, options, field, key, val);
                    break;
            }
        }
        let u = options.writeUnknownFields;
        if (u !== false)
            (u === true ? UnknownFieldHandler.onWrite : u)(this.info.typeName, message, writer);
    }
    mapEntry(writer, options, field, key, value) {
        writer.tag(field.no, WireType.LengthDelimited);
        writer.fork();
        // javascript only allows number or string for object properties
        // we convert from our representation to the protobuf type
        let keyValue = key;
        switch (field.K) {
            case ScalarType.INT32:
            case ScalarType.FIXED32:
            case ScalarType.UINT32:
            case ScalarType.SFIXED32:
            case ScalarType.SINT32:
                keyValue = Number.parseInt(key);
                break;
            case ScalarType.BOOL:
                assert(key == 'true' || key == 'false');
                keyValue = key == 'true';
                break;
        }
        // write key, expecting key field number = 1
        this.scalar(writer, field.K, 1, keyValue, true);
        // write value, expecting value field number = 2
        switch (field.V.kind) {
            case 'scalar':
                this.scalar(writer, field.V.T, 2, value, true);
                break;
            case 'enum':
                this.scalar(writer, ScalarType.INT32, 2, value, true);
                break;
            case 'message':
                this.message(writer, options, field.V.T(), 2, value);
                break;
        }
        writer.join();
    }
    message(writer, options, handler, fieldNo, value) {
        if (value === undefined)
            return;
        handler.internalBinaryWrite(value, writer.tag(fieldNo, WireType.LengthDelimited).fork(), options);
        writer.join();
    }
    /**
     * Write a single scalar value.
     */
    scalar(writer, type, fieldNo, value, emitDefault) {
        let [wireType, method, isDefault] = this.scalarInfo(type, value);
        if (!isDefault || emitDefault) {
            writer.tag(fieldNo, wireType);
            writer[method](value);
        }
    }
    /**
     * Write an array of scalar values in packed format.
     */
    packed(writer, type, fieldNo, value) {
        if (!value.length)
            return;
        assert(type !== ScalarType.BYTES && type !== ScalarType.STRING);
        // write tag
        writer.tag(fieldNo, WireType.LengthDelimited);
        // begin length-delimited
        writer.fork();
        // write values without tags
        let [, method,] = this.scalarInfo(type);
        for (let i = 0; i < value.length; i++)
            writer[method](value[i]);
        // end length delimited
        writer.join();
    }
    /**
     * Get information for writing a scalar value.
     *
     * Returns tuple:
     * [0]: appropriate WireType
     * [1]: name of the appropriate method of IBinaryWriter
     * [2]: whether the given value is a default value
     *
     * If argument `value` is omitted, [2] is always false.
     */
    scalarInfo(type, value) {
        let t = WireType.Varint;
        let m;
        let i = value === undefined;
        let d = value === 0;
        switch (type) {
            case ScalarType.INT32:
                m = "int32";
                break;
            case ScalarType.STRING:
                d = i || !value.length;
                t = WireType.LengthDelimited;
                m = "string";
                break;
            case ScalarType.BOOL:
                d = value === false;
                m = "bool";
                break;
            case ScalarType.UINT32:
                m = "uint32";
                break;
            case ScalarType.DOUBLE:
                t = WireType.Bit64;
                m = "double";
                break;
            case ScalarType.FLOAT:
                t = WireType.Bit32;
                m = "float";
                break;
            case ScalarType.INT64:
                d = i || PbLong.from(value).isZero();
                m = "int64";
                break;
            case ScalarType.UINT64:
                d = i || PbULong.from(value).isZero();
                m = "uint64";
                break;
            case ScalarType.FIXED64:
                d = i || PbULong.from(value).isZero();
                t = WireType.Bit64;
                m = "fixed64";
                break;
            case ScalarType.BYTES:
                d = i || !value.byteLength;
                t = WireType.LengthDelimited;
                m = "bytes";
                break;
            case ScalarType.FIXED32:
                t = WireType.Bit32;
                m = "fixed32";
                break;
            case ScalarType.SFIXED32:
                t = WireType.Bit32;
                m = "sfixed32";
                break;
            case ScalarType.SFIXED64:
                d = i || PbLong.from(value).isZero();
                t = WireType.Bit64;
                m = "sfixed64";
                break;
            case ScalarType.SINT32:
                m = "sint32";
                break;
            case ScalarType.SINT64:
                d = i || PbLong.from(value).isZero();
                m = "sint64";
                break;
        }
        return [t, m, i || d];
    }
}

/**
 * Creates an instance of the generic message, using the field
 * information.
 */
function reflectionCreate(type) {
    /**
     * This ternary can be removed in the next major version.
     * The `Object.create()` code path utilizes a new `messagePrototype`
     * property on the `IMessageType` which has this same `MESSAGE_TYPE`
     * non-enumerable property on it. Doing it this way means that we only
     * pay the cost of `Object.defineProperty()` once per `IMessageType`
     * class of once per "instance". The falsy code path is only provided
     * for backwards compatibility in cases where the runtime library is
     * updated without also updating the generated code.
     */
    const msg = type.messagePrototype
        ? Object.create(type.messagePrototype)
        : Object.defineProperty({}, MESSAGE_TYPE, { value: type });
    for (let field of type.fields) {
        let name = field.localName;
        if (field.opt)
            continue;
        if (field.oneof)
            msg[field.oneof] = { oneofKind: undefined };
        else if (field.repeat)
            msg[name] = [];
        else
            switch (field.kind) {
                case "scalar":
                    msg[name] = reflectionScalarDefault(field.T, field.L);
                    break;
                case "enum":
                    // we require 0 to be default value for all enums
                    msg[name] = 0;
                    break;
                case "map":
                    msg[name] = {};
                    break;
            }
    }
    return msg;
}

/**
 * Copy partial data into the target message.
 *
 * If a singular scalar or enum field is present in the source, it
 * replaces the field in the target.
 *
 * If a singular message field is present in the source, it is merged
 * with the target field by calling mergePartial() of the responsible
 * message type.
 *
 * If a repeated field is present in the source, its values replace
 * all values in the target array, removing extraneous values.
 * Repeated message fields are copied, not merged.
 *
 * If a map field is present in the source, entries are added to the
 * target map, replacing entries with the same key. Entries that only
 * exist in the target remain. Entries with message values are copied,
 * not merged.
 *
 * Note that this function differs from protobuf merge semantics,
 * which appends repeated fields.
 */
function reflectionMergePartial(info, target, source) {
    let fieldValue, // the field value we are working with
    input = source, output; // where we want our field value to go
    for (let field of info.fields) {
        let name = field.localName;
        if (field.oneof) {
            const group = input[field.oneof]; // this is the oneof`s group in the source
            if ((group === null || group === void 0 ? void 0 : group.oneofKind) == undefined) { // the user is free to omit
                continue; // we skip this field, and all other members too
            }
            fieldValue = group[name]; // our value comes from the the oneof group of the source
            output = target[field.oneof]; // and our output is the oneof group of the target
            output.oneofKind = group.oneofKind; // always update discriminator
            if (fieldValue == undefined) {
                delete output[name]; // remove any existing value
                continue; // skip further work on field
            }
        }
        else {
            fieldValue = input[name]; // we are using the source directly
            output = target; // we want our field value to go directly into the target
            if (fieldValue == undefined) {
                continue; // skip further work on field, existing value is used as is
            }
        }
        if (field.repeat)
            output[name].length = fieldValue.length; // resize target array to match source array
        // now we just work with `fieldValue` and `output` to merge the value
        switch (field.kind) {
            case "scalar":
            case "enum":
                if (field.repeat)
                    for (let i = 0; i < fieldValue.length; i++)
                        output[name][i] = fieldValue[i]; // not a reference type
                else
                    output[name] = fieldValue; // not a reference type
                break;
            case "message":
                let T = field.T();
                if (field.repeat)
                    for (let i = 0; i < fieldValue.length; i++)
                        output[name][i] = T.create(fieldValue[i]);
                else if (output[name] === undefined)
                    output[name] = T.create(fieldValue); // nothing to merge with
                else
                    T.mergePartial(output[name], fieldValue);
                break;
            case "map":
                // Map and repeated fields are simply overwritten, not appended or merged
                switch (field.V.kind) {
                    case "scalar":
                    case "enum":
                        Object.assign(output[name], fieldValue); // elements are not reference types
                        break;
                    case "message":
                        let T = field.V.T();
                        for (let k of Object.keys(fieldValue))
                            output[name][k] = T.create(fieldValue[k]);
                        break;
                }
                break;
        }
    }
}

/**
 * Determines whether two message of the same type have the same field values.
 * Checks for deep equality, traversing repeated fields, oneof groups, maps
 * and messages recursively.
 * Will also return true if both messages are `undefined`.
 */
function reflectionEquals(info, a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    for (let field of info.fields) {
        let localName = field.localName;
        let val_a = field.oneof ? a[field.oneof][localName] : a[localName];
        let val_b = field.oneof ? b[field.oneof][localName] : b[localName];
        switch (field.kind) {
            case "enum":
            case "scalar":
                let t = field.kind == "enum" ? ScalarType.INT32 : field.T;
                if (!(field.repeat
                    ? repeatedPrimitiveEq(t, val_a, val_b)
                    : primitiveEq(t, val_a, val_b)))
                    return false;
                break;
            case "map":
                if (!(field.V.kind == "message"
                    ? repeatedMsgEq(field.V.T(), objectValues(val_a), objectValues(val_b))
                    : repeatedPrimitiveEq(field.V.kind == "enum" ? ScalarType.INT32 : field.V.T, objectValues(val_a), objectValues(val_b))))
                    return false;
                break;
            case "message":
                let T = field.T();
                if (!(field.repeat
                    ? repeatedMsgEq(T, val_a, val_b)
                    : T.equals(val_a, val_b)))
                    return false;
                break;
        }
    }
    return true;
}
const objectValues = Object.values;
function primitiveEq(type, a, b) {
    if (a === b)
        return true;
    if (type !== ScalarType.BYTES)
        return false;
    let ba = a;
    let bb = b;
    if (ba.length !== bb.length)
        return false;
    for (let i = 0; i < ba.length; i++)
        if (ba[i] != bb[i])
            return false;
    return true;
}
function repeatedPrimitiveEq(type, a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (!primitiveEq(type, a[i], b[i]))
            return false;
    return true;
}
function repeatedMsgEq(type, a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++)
        if (!type.equals(a[i], b[i]))
            return false;
    return true;
}

const baseDescriptors = Object.getOwnPropertyDescriptors(Object.getPrototypeOf({}));
const messageTypeDescriptor = baseDescriptors[MESSAGE_TYPE] = {};
/**
 * This standard message type provides reflection-based
 * operations to work with a message.
 */
class MessageType {
    constructor(name, fields, options) {
        this.defaultCheckDepth = 16;
        this.typeName = name;
        this.fields = fields.map(normalizeFieldInfo);
        this.options = options !== null && options !== void 0 ? options : {};
        messageTypeDescriptor.value = this;
        this.messagePrototype = Object.create(null, baseDescriptors);
        this.refTypeCheck = new ReflectionTypeCheck(this);
        this.refJsonReader = new ReflectionJsonReader(this);
        this.refJsonWriter = new ReflectionJsonWriter(this);
        this.refBinReader = new ReflectionBinaryReader(this);
        this.refBinWriter = new ReflectionBinaryWriter(this);
    }
    create(value) {
        let message = reflectionCreate(this);
        if (value !== undefined) {
            reflectionMergePartial(this, message, value);
        }
        return message;
    }
    /**
     * Clone the message.
     *
     * Unknown fields are discarded.
     */
    clone(message) {
        let copy = this.create();
        reflectionMergePartial(this, copy, message);
        return copy;
    }
    /**
     * Determines whether two message of the same type have the same field values.
     * Checks for deep equality, traversing repeated fields, oneof groups, maps
     * and messages recursively.
     * Will also return true if both messages are `undefined`.
     */
    equals(a, b) {
        return reflectionEquals(this, a, b);
    }
    /**
     * Is the given value assignable to our message type
     * and contains no [excess properties](https://www.typescriptlang.org/docs/handbook/interfaces.html#excess-property-checks)?
     */
    is(arg, depth = this.defaultCheckDepth) {
        return this.refTypeCheck.is(arg, depth, false);
    }
    /**
     * Is the given value assignable to our message type,
     * regardless of [excess properties](https://www.typescriptlang.org/docs/handbook/interfaces.html#excess-property-checks)?
     */
    isAssignable(arg, depth = this.defaultCheckDepth) {
        return this.refTypeCheck.is(arg, depth, true);
    }
    /**
     * Copy partial data into the target message.
     */
    mergePartial(target, source) {
        reflectionMergePartial(this, target, source);
    }
    /**
     * Create a new message from binary format.
     */
    fromBinary(data, options) {
        let opt = binaryReadOptions(options);
        return this.internalBinaryRead(opt.readerFactory(data), data.byteLength, opt);
    }
    /**
     * Read a new message from a JSON value.
     */
    fromJson(json, options) {
        return this.internalJsonRead(json, jsonReadOptions(options));
    }
    /**
     * Read a new message from a JSON string.
     * This is equivalent to `T.fromJson(JSON.parse(json))`.
     */
    fromJsonString(json, options) {
        let value = JSON.parse(json);
        return this.fromJson(value, options);
    }
    /**
     * Write the message to canonical JSON value.
     */
    toJson(message, options) {
        return this.internalJsonWrite(message, jsonWriteOptions(options));
    }
    /**
     * Convert the message to canonical JSON string.
     * This is equivalent to `JSON.stringify(T.toJson(t))`
     */
    toJsonString(message, options) {
        var _a;
        let value = this.toJson(message, options);
        return JSON.stringify(value, null, (_a = options === null || options === void 0 ? void 0 : options.prettySpaces) !== null && _a !== void 0 ? _a : 0);
    }
    /**
     * Write the message to binary format.
     */
    toBinary(message, options) {
        let opt = binaryWriteOptions(options);
        return this.internalBinaryWrite(message, opt.writerFactory(), opt).finish();
    }
    /**
     * This is an internal method. If you just want to read a message from
     * JSON, use `fromJson()` or `fromJsonString()`.
     *
     * Reads JSON value and merges the fields into the target
     * according to protobuf rules. If the target is omitted,
     * a new instance is created first.
     */
    internalJsonRead(json, options, target) {
        if (json !== null && typeof json == "object" && !Array.isArray(json)) {
            let message = target !== null && target !== void 0 ? target : this.create();
            this.refJsonReader.read(json, message, options);
            return message;
        }
        throw new Error(`Unable to parse message ${this.typeName} from JSON ${typeofJsonValue(json)}.`);
    }
    /**
     * This is an internal method. If you just want to write a message
     * to JSON, use `toJson()` or `toJsonString().
     *
     * Writes JSON value and returns it.
     */
    internalJsonWrite(message, options) {
        return this.refJsonWriter.write(message, options);
    }
    /**
     * This is an internal method. If you just want to write a message
     * in binary format, use `toBinary()`.
     *
     * Serializes the message in binary format and appends it to the given
     * writer. Returns passed writer.
     */
    internalBinaryWrite(message, writer, options) {
        this.refBinWriter.write(message, writer, options);
        return writer;
    }
    /**
     * This is an internal method. If you just want to read a message from
     * binary data, use `fromBinary()`.
     *
     * Reads data from binary format and merges the fields into
     * the target according to protobuf rules. If the target is
     * omitted, a new instance is created first.
     */
    internalBinaryRead(reader, length, options, target) {
        let message = target !== null && target !== void 0 ? target : this.create();
        this.refBinReader.read(reader, message, options, length);
        return message;
    }
}

// @generated message type with reflection information, may provide speed optimized methods
class RecordingVideoConfig$Type extends MessageType {
    constructor() {
        super("RecordingVideoConfig", [
            { no: 1, name: "codec", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "codedWidth", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 3, name: "codedHeight", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 4, name: "rotation", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 5, name: "extradata", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.codec = "";
        message.codedWidth = 0;
        message.codedHeight = 0;
        message.rotation = 0;
        message.extradata = new Uint8Array(0);
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* string codec */ 1:
                    message.codec = reader.string();
                    break;
                case /* uint32 codedWidth */ 2:
                    message.codedWidth = reader.uint32();
                    break;
                case /* uint32 codedHeight */ 3:
                    message.codedHeight = reader.uint32();
                    break;
                case /* uint32 rotation */ 4:
                    message.rotation = reader.uint32();
                    break;
                case /* bytes extradata */ 5:
                    message.extradata = reader.bytes();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* string codec = 1; */
        if (message.codec !== "")
            writer.tag(1, WireType.LengthDelimited).string(message.codec);
        /* uint32 codedWidth = 2; */
        if (message.codedWidth !== 0)
            writer.tag(2, WireType.Varint).uint32(message.codedWidth);
        /* uint32 codedHeight = 3; */
        if (message.codedHeight !== 0)
            writer.tag(3, WireType.Varint).uint32(message.codedHeight);
        /* uint32 rotation = 4; */
        if (message.rotation !== 0)
            writer.tag(4, WireType.Varint).uint32(message.rotation);
        /* bytes extradata = 5; */
        if (message.extradata.length)
            writer.tag(5, WireType.LengthDelimited).bytes(message.extradata);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingVideoConfig
 */
const RecordingVideoConfig = new RecordingVideoConfig$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecordingAudioConfig$Type extends MessageType {
    constructor() {
        super("RecordingAudioConfig", [
            { no: 1, name: "codec", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "sampleRate", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 3, name: "channels", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 4, name: "preSkip", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 5, name: "frameDurationUs", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 6, name: "silenceFrame", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.codec = "";
        message.sampleRate = 0;
        message.channels = 0;
        message.preSkip = 0;
        message.frameDurationUs = 0;
        message.silenceFrame = new Uint8Array(0);
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* string codec */ 1:
                    message.codec = reader.string();
                    break;
                case /* uint32 sampleRate */ 2:
                    message.sampleRate = reader.uint32();
                    break;
                case /* uint32 channels */ 3:
                    message.channels = reader.uint32();
                    break;
                case /* uint32 preSkip */ 4:
                    message.preSkip = reader.uint32();
                    break;
                case /* uint32 frameDurationUs */ 5:
                    message.frameDurationUs = reader.uint32();
                    break;
                case /* bytes silenceFrame */ 6:
                    message.silenceFrame = reader.bytes();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* string codec = 1; */
        if (message.codec !== "")
            writer.tag(1, WireType.LengthDelimited).string(message.codec);
        /* uint32 sampleRate = 2; */
        if (message.sampleRate !== 0)
            writer.tag(2, WireType.Varint).uint32(message.sampleRate);
        /* uint32 channels = 3; */
        if (message.channels !== 0)
            writer.tag(3, WireType.Varint).uint32(message.channels);
        /* uint32 preSkip = 4; */
        if (message.preSkip !== 0)
            writer.tag(4, WireType.Varint).uint32(message.preSkip);
        /* uint32 frameDurationUs = 5; */
        if (message.frameDurationUs !== 0)
            writer.tag(5, WireType.Varint).uint32(message.frameDurationUs);
        /* bytes silenceFrame = 6; */
        if (message.silenceFrame.length)
            writer.tag(6, WireType.LengthDelimited).bytes(message.silenceFrame);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingAudioConfig
 */
const RecordingAudioConfig = new RecordingAudioConfig$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecordingSegmentEntry$Type extends MessageType {
    constructor() {
        super("RecordingSegmentEntry", [
            { no: 1, name: "index", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 2, name: "partIndex", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 3, name: "partition", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 4, name: "packetBytes", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 5, name: "sha256", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 6, name: "firstPacketNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 7, name: "videoStartUs", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 8, name: "videoSizes", kind: "scalar", repeat: 1 /*RepeatType.PACKED*/, T: 13 /*ScalarType.UINT32*/ },
            { no: 9, name: "videoTsDeltasUs", kind: "scalar", repeat: 1 /*RepeatType.PACKED*/, T: 18 /*ScalarType.SINT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 10, name: "videoKeyIndexes", kind: "scalar", repeat: 1 /*RepeatType.PACKED*/, T: 13 /*ScalarType.UINT32*/ },
            { no: 11, name: "audioStartUs", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 12, name: "audioSizes", kind: "scalar", repeat: 1 /*RepeatType.PACKED*/, T: 13 /*ScalarType.UINT32*/ },
            { no: 13, name: "audioTsDeltasUs", kind: "scalar", repeat: 1 /*RepeatType.PACKED*/, T: 18 /*ScalarType.SINT64*/, L: 2 /*LongType.NUMBER*/ }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.index = 0;
        message.partIndex = 0;
        message.partition = 0;
        message.packetBytes = 0;
        message.sha256 = new Uint8Array(0);
        message.firstPacketNumber = 0;
        message.videoStartUs = 0;
        message.videoSizes = [];
        message.videoTsDeltasUs = [];
        message.videoKeyIndexes = [];
        message.audioStartUs = 0;
        message.audioSizes = [];
        message.audioTsDeltasUs = [];
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* uint32 index */ 1:
                    message.index = reader.uint32();
                    break;
                case /* uint32 partIndex */ 2:
                    message.partIndex = reader.uint32();
                    break;
                case /* uint32 partition */ 3:
                    message.partition = reader.uint32();
                    break;
                case /* uint32 packetBytes */ 4:
                    message.packetBytes = reader.uint32();
                    break;
                case /* bytes sha256 */ 5:
                    message.sha256 = reader.bytes();
                    break;
                case /* int64 firstPacketNumber */ 6:
                    message.firstPacketNumber = reader.int64().toNumber();
                    break;
                case /* int64 videoStartUs */ 7:
                    message.videoStartUs = reader.int64().toNumber();
                    break;
                case /* repeated uint32 videoSizes */ 8:
                    if (wireType === WireType.LengthDelimited)
                        for (let e = reader.int32() + reader.pos; reader.pos < e;)
                            message.videoSizes.push(reader.uint32());
                    else
                        message.videoSizes.push(reader.uint32());
                    break;
                case /* repeated sint64 videoTsDeltasUs */ 9:
                    if (wireType === WireType.LengthDelimited)
                        for (let e = reader.int32() + reader.pos; reader.pos < e;)
                            message.videoTsDeltasUs.push(reader.sint64().toNumber());
                    else
                        message.videoTsDeltasUs.push(reader.sint64().toNumber());
                    break;
                case /* repeated uint32 videoKeyIndexes */ 10:
                    if (wireType === WireType.LengthDelimited)
                        for (let e = reader.int32() + reader.pos; reader.pos < e;)
                            message.videoKeyIndexes.push(reader.uint32());
                    else
                        message.videoKeyIndexes.push(reader.uint32());
                    break;
                case /* int64 audioStartUs */ 11:
                    message.audioStartUs = reader.int64().toNumber();
                    break;
                case /* repeated uint32 audioSizes */ 12:
                    if (wireType === WireType.LengthDelimited)
                        for (let e = reader.int32() + reader.pos; reader.pos < e;)
                            message.audioSizes.push(reader.uint32());
                    else
                        message.audioSizes.push(reader.uint32());
                    break;
                case /* repeated sint64 audioTsDeltasUs */ 13:
                    if (wireType === WireType.LengthDelimited)
                        for (let e = reader.int32() + reader.pos; reader.pos < e;)
                            message.audioTsDeltasUs.push(reader.sint64().toNumber());
                    else
                        message.audioTsDeltasUs.push(reader.sint64().toNumber());
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* uint32 index = 1; */
        if (message.index !== 0)
            writer.tag(1, WireType.Varint).uint32(message.index);
        /* uint32 partIndex = 2; */
        if (message.partIndex !== 0)
            writer.tag(2, WireType.Varint).uint32(message.partIndex);
        /* uint32 partition = 3; */
        if (message.partition !== 0)
            writer.tag(3, WireType.Varint).uint32(message.partition);
        /* uint32 packetBytes = 4; */
        if (message.packetBytes !== 0)
            writer.tag(4, WireType.Varint).uint32(message.packetBytes);
        /* bytes sha256 = 5; */
        if (message.sha256.length)
            writer.tag(5, WireType.LengthDelimited).bytes(message.sha256);
        /* int64 firstPacketNumber = 6; */
        if (message.firstPacketNumber !== 0)
            writer.tag(6, WireType.Varint).int64(message.firstPacketNumber);
        /* int64 videoStartUs = 7; */
        if (message.videoStartUs !== 0)
            writer.tag(7, WireType.Varint).int64(message.videoStartUs);
        /* repeated uint32 videoSizes = 8; */
        if (message.videoSizes.length) {
            writer.tag(8, WireType.LengthDelimited).fork();
            for (let i = 0; i < message.videoSizes.length; i++)
                writer.uint32(message.videoSizes[i]);
            writer.join();
        }
        /* repeated sint64 videoTsDeltasUs = 9; */
        if (message.videoTsDeltasUs.length) {
            writer.tag(9, WireType.LengthDelimited).fork();
            for (let i = 0; i < message.videoTsDeltasUs.length; i++)
                writer.sint64(message.videoTsDeltasUs[i]);
            writer.join();
        }
        /* repeated uint32 videoKeyIndexes = 10; */
        if (message.videoKeyIndexes.length) {
            writer.tag(10, WireType.LengthDelimited).fork();
            for (let i = 0; i < message.videoKeyIndexes.length; i++)
                writer.uint32(message.videoKeyIndexes[i]);
            writer.join();
        }
        /* int64 audioStartUs = 11; */
        if (message.audioStartUs !== 0)
            writer.tag(11, WireType.Varint).int64(message.audioStartUs);
        /* repeated uint32 audioSizes = 12; */
        if (message.audioSizes.length) {
            writer.tag(12, WireType.LengthDelimited).fork();
            for (let i = 0; i < message.audioSizes.length; i++)
                writer.uint32(message.audioSizes[i]);
            writer.join();
        }
        /* repeated sint64 audioTsDeltasUs = 13; */
        if (message.audioTsDeltasUs.length) {
            writer.tag(13, WireType.LengthDelimited).fork();
            for (let i = 0; i < message.audioTsDeltasUs.length; i++)
                writer.sint64(message.audioTsDeltasUs[i]);
            writer.join();
        }
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingSegmentEntry
 */
const RecordingSegmentEntry = new RecordingSegmentEntry$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecordingPart$Type extends MessageType {
    constructor() {
        super("RecordingPart", [
            { no: 1, name: "index", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 2, name: "video", kind: "message", T: () => RecordingVideoConfig },
            { no: 3, name: "audio", kind: "message", T: () => RecordingAudioConfig },
            { no: 4, name: "segments", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => RecordingSegmentEntry }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.index = 0;
        message.segments = [];
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* uint32 index */ 1:
                    message.index = reader.uint32();
                    break;
                case /* RecordingVideoConfig video */ 2:
                    message.video = RecordingVideoConfig.internalBinaryRead(reader, reader.uint32(), options, message.video);
                    break;
                case /* RecordingAudioConfig audio */ 3:
                    message.audio = RecordingAudioConfig.internalBinaryRead(reader, reader.uint32(), options, message.audio);
                    break;
                case /* repeated RecordingSegmentEntry segments */ 4:
                    message.segments.push(RecordingSegmentEntry.internalBinaryRead(reader, reader.uint32(), options));
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* uint32 index = 1; */
        if (message.index !== 0)
            writer.tag(1, WireType.Varint).uint32(message.index);
        /* RecordingVideoConfig video = 2; */
        if (message.video)
            RecordingVideoConfig.internalBinaryWrite(message.video, writer.tag(2, WireType.LengthDelimited).fork(), options).join();
        /* RecordingAudioConfig audio = 3; */
        if (message.audio)
            RecordingAudioConfig.internalBinaryWrite(message.audio, writer.tag(3, WireType.LengthDelimited).fork(), options).join();
        /* repeated RecordingSegmentEntry segments = 4; */
        for (let i = 0; i < message.segments.length; i++)
            RecordingSegmentEntry.internalBinaryWrite(message.segments[i], writer.tag(4, WireType.LengthDelimited).fork(), options).join();
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingPart
 */
const RecordingPart = new RecordingPart$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecordingIndex$Type extends MessageType {
    constructor() {
        super("RecordingIndex", [
            { no: 1, name: "version", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 2, name: "wireFormatVersion", kind: "scalar", T: 13 /*ScalarType.UINT32*/ },
            { no: 3, name: "recordingId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "streamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 5, name: "broadcasterUserId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 6, name: "createdAtMs", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 7, name: "complete", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 8, name: "parts", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => RecordingPart }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.version = 0;
        message.wireFormatVersion = 0;
        message.recordingId = "";
        message.streamId = "";
        message.broadcasterUserId = "";
        message.createdAtMs = 0;
        message.complete = false;
        message.parts = [];
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* uint32 version */ 1:
                    message.version = reader.uint32();
                    break;
                case /* uint32 wireFormatVersion */ 2:
                    message.wireFormatVersion = reader.uint32();
                    break;
                case /* string recordingId */ 3:
                    message.recordingId = reader.string();
                    break;
                case /* string streamId */ 4:
                    message.streamId = reader.string();
                    break;
                case /* string broadcasterUserId */ 5:
                    message.broadcasterUserId = reader.string();
                    break;
                case /* int64 createdAtMs */ 6:
                    message.createdAtMs = reader.int64().toNumber();
                    break;
                case /* bool complete */ 7:
                    message.complete = reader.bool();
                    break;
                case /* repeated RecordingPart parts */ 8:
                    message.parts.push(RecordingPart.internalBinaryRead(reader, reader.uint32(), options));
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* uint32 version = 1; */
        if (message.version !== 0)
            writer.tag(1, WireType.Varint).uint32(message.version);
        /* uint32 wireFormatVersion = 2; */
        if (message.wireFormatVersion !== 0)
            writer.tag(2, WireType.Varint).uint32(message.wireFormatVersion);
        /* string recordingId = 3; */
        if (message.recordingId !== "")
            writer.tag(3, WireType.LengthDelimited).string(message.recordingId);
        /* string streamId = 4; */
        if (message.streamId !== "")
            writer.tag(4, WireType.LengthDelimited).string(message.streamId);
        /* string broadcasterUserId = 5; */
        if (message.broadcasterUserId !== "")
            writer.tag(5, WireType.LengthDelimited).string(message.broadcasterUserId);
        /* int64 createdAtMs = 6; */
        if (message.createdAtMs !== 0)
            writer.tag(6, WireType.Varint).int64(message.createdAtMs);
        /* bool complete = 7; */
        if (message.complete !== false)
            writer.tag(7, WireType.Varint).bool(message.complete);
        /* repeated RecordingPart parts = 8; */
        for (let i = 0; i < message.parts.length; i++)
            RecordingPart.internalBinaryWrite(message.parts[i], writer.tag(8, WireType.LengthDelimited).fork(), options).join();
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingIndex
 */
const RecordingIndex = new RecordingIndex$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecordingSegmentTrailer$Type extends MessageType {
    constructor() {
        super("RecordingSegmentTrailer", [
            { no: 1, name: "recordingId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "entry", kind: "message", T: () => RecordingSegmentEntry },
            { no: 3, name: "video", kind: "message", T: () => RecordingVideoConfig },
            { no: 4, name: "audio", kind: "message", T: () => RecordingAudioConfig }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.recordingId = "";
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* string recordingId */ 1:
                    message.recordingId = reader.string();
                    break;
                case /* RecordingSegmentEntry entry */ 2:
                    message.entry = RecordingSegmentEntry.internalBinaryRead(reader, reader.uint32(), options, message.entry);
                    break;
                case /* RecordingVideoConfig video */ 3:
                    message.video = RecordingVideoConfig.internalBinaryRead(reader, reader.uint32(), options, message.video);
                    break;
                case /* RecordingAudioConfig audio */ 4:
                    message.audio = RecordingAudioConfig.internalBinaryRead(reader, reader.uint32(), options, message.audio);
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* string recordingId = 1; */
        if (message.recordingId !== "")
            writer.tag(1, WireType.LengthDelimited).string(message.recordingId);
        /* RecordingSegmentEntry entry = 2; */
        if (message.entry)
            RecordingSegmentEntry.internalBinaryWrite(message.entry, writer.tag(2, WireType.LengthDelimited).fork(), options).join();
        /* RecordingVideoConfig video = 3; */
        if (message.video)
            RecordingVideoConfig.internalBinaryWrite(message.video, writer.tag(3, WireType.LengthDelimited).fork(), options).join();
        /* RecordingAudioConfig audio = 4; */
        if (message.audio)
            RecordingAudioConfig.internalBinaryWrite(message.audio, writer.tag(4, WireType.LengthDelimited).fork(), options).join();
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message RecordingSegmentTrailer
 */
const RecordingSegmentTrailer = new RecordingSegmentTrailer$Type();
// @generated message type with reflection information, may provide speed optimized methods
class WireMessageId$Type extends MessageType {
    constructor() {
        super("WireMessageId", [
            { no: 1, name: "streamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "streamPartition", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 3, name: "timestamp", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 2 /*LongType.NUMBER*/ },
            { no: 4, name: "sequenceNumber", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 5, name: "publisherId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 6, name: "messageChainId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.streamId = "";
        message.streamPartition = 0;
        message.timestamp = 0;
        message.sequenceNumber = 0;
        message.publisherId = new Uint8Array(0);
        message.messageChainId = "";
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* string streamId */ 1:
                    message.streamId = reader.string();
                    break;
                case /* int32 streamPartition */ 2:
                    message.streamPartition = reader.int32();
                    break;
                case /* int64 timestamp */ 3:
                    message.timestamp = reader.int64().toNumber();
                    break;
                case /* int32 sequenceNumber */ 4:
                    message.sequenceNumber = reader.int32();
                    break;
                case /* bytes publisherId */ 5:
                    message.publisherId = reader.bytes();
                    break;
                case /* string messageChainId */ 6:
                    message.messageChainId = reader.string();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* string streamId = 1; */
        if (message.streamId !== "")
            writer.tag(1, WireType.LengthDelimited).string(message.streamId);
        /* int32 streamPartition = 2; */
        if (message.streamPartition !== 0)
            writer.tag(2, WireType.Varint).int32(message.streamPartition);
        /* int64 timestamp = 3; */
        if (message.timestamp !== 0)
            writer.tag(3, WireType.Varint).int64(message.timestamp);
        /* int32 sequenceNumber = 4; */
        if (message.sequenceNumber !== 0)
            writer.tag(4, WireType.Varint).int32(message.sequenceNumber);
        /* bytes publisherId = 5; */
        if (message.publisherId.length)
            writer.tag(5, WireType.LengthDelimited).bytes(message.publisherId);
        /* string messageChainId = 6; */
        if (message.messageChainId !== "")
            writer.tag(6, WireType.LengthDelimited).string(message.messageChainId);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message WireMessageId
 */
const WireMessageId = new WireMessageId$Type();
// @generated message type with reflection information, may provide speed optimized methods
class WireContentMessage$Type extends MessageType {
    constructor() {
        super("WireContentMessage", [
            { no: 1, name: "content", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.content = new Uint8Array(0);
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* bytes content */ 1:
                    message.content = reader.bytes();
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* bytes content = 1; */
        if (message.content.length)
            writer.tag(1, WireType.LengthDelimited).bytes(message.content);
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message WireContentMessage
 */
const WireContentMessage = new WireContentMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class WireStreamMessage$Type extends MessageType {
    constructor() {
        super("WireStreamMessage", [
            { no: 1, name: "messageId", kind: "message", T: () => WireMessageId },
            { no: 3, name: "signature", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 5, name: "contentMessage", kind: "message", T: () => WireContentMessage }
        ]);
    }
    create(value) {
        const message = globalThis.Object.create((this.messagePrototype));
        message.signature = new Uint8Array(0);
        if (value !== undefined)
            reflectionMergePartial(this, message, value);
        return message;
    }
    internalBinaryRead(reader, length, options, target) {
        let message = target ?? this.create(), end = reader.pos + length;
        while (reader.pos < end) {
            let [fieldNo, wireType] = reader.tag();
            switch (fieldNo) {
                case /* WireMessageId messageId */ 1:
                    message.messageId = WireMessageId.internalBinaryRead(reader, reader.uint32(), options, message.messageId);
                    break;
                case /* bytes signature */ 3:
                    message.signature = reader.bytes();
                    break;
                case /* WireContentMessage contentMessage */ 5:
                    message.contentMessage = WireContentMessage.internalBinaryRead(reader, reader.uint32(), options, message.contentMessage);
                    break;
                default:
                    let u = options.readUnknownField;
                    if (u === "throw")
                        throw new globalThis.Error(`Unknown field ${fieldNo} (wire type ${wireType}) for ${this.typeName}`);
                    let d = reader.skip(wireType);
                    if (u !== false)
                        (u === true ? UnknownFieldHandler.onRead : u)(this.typeName, message, fieldNo, wireType, d);
            }
        }
        return message;
    }
    internalBinaryWrite(message, writer, options) {
        /* WireMessageId messageId = 1; */
        if (message.messageId)
            WireMessageId.internalBinaryWrite(message.messageId, writer.tag(1, WireType.LengthDelimited).fork(), options).join();
        /* bytes signature = 3; */
        if (message.signature.length)
            writer.tag(3, WireType.LengthDelimited).bytes(message.signature);
        /* WireContentMessage contentMessage = 5; */
        if (message.contentMessage)
            WireContentMessage.internalBinaryWrite(message.contentMessage, writer.tag(5, WireType.LengthDelimited).fork(), options).join();
        let u = options.writeUnknownFields;
        if (u !== false)
            (u == true ? UnknownFieldHandler.onWrite : u)(this.typeName, message, writer);
        return writer;
    }
}
/**
 * @generated MessageType for protobuf message WireStreamMessage
 */
const WireStreamMessage = new WireStreamMessage$Type();

/** Small byte helpers shared by the recording format code. No DOM, no Node. */
function concatBytes(parts) {
    let total = 0;
    for (const part of parts) {
        total += part.byteLength;
    }
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.byteLength;
    }
    return out;
}
function writeU32(value) {
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, value, false);
    return out;
}
function readU32(bytes, offset) {
    return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(offset, false);
}
function bytesEqual(a, b) {
    if (a.byteLength !== b.byteLength) {
        return false;
    }
    for (let i = 0; i < a.byteLength; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}
function utf8(text) {
    return new TextEncoder().encode(text);
}
function bytesToHex(bytes) {
    let out = '';
    for (const byte of bytes) {
        out += byte.toString(16).padStart(2, '0');
    }
    return out;
}
function hexToBytes(hex) {
    const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
    if (clean.length % 2 !== 0 || /[^0-9a-fA-F]/.test(clean)) {
        throw new Error('hexToBytes: not a hex string');
    }
    const out = new Uint8Array(clean.length / 2);
    for (let i = 0; i < out.byteLength; i++) {
        out[i] = Number.parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}
function bytesToBase64Url(bytes) {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary)
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replaceAll('=', '');
}
function base64UrlToBytes(text) {
    const padded = text.replaceAll('-', '+').replaceAll('_', '/');
    const binary = atob(padded);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        out[i] = binary.charCodeAt(i);
    }
    return out;
}
function randomBytes(length) {
    return crypto.getRandomValues(new Uint8Array(length));
}
/**
 * WebCrypto wants views over a plain ArrayBuffer. Views over a
 * SharedArrayBuffer (or typed loosely) are copied.
 */
function asBufferSource(bytes) {
    if (bytes.buffer instanceof ArrayBuffer) {
        return bytes;
    }
    return new Uint8Array(bytes);
}
async function sha256(bytes) {
    return new Uint8Array(await crypto.subtle.digest('SHA-256', asBufferSource(bytes)));
}
async function sha256Hex(bytes) {
    return bytesToHex(await sha256(bytes));
}

/**
 * Signed requests to the recording backend. Same construction as the TURN
 * credential request (turncredential/turnCredential.ts): a canonical
 * payload with fixed field order, signed with the broadcaster's key, with a
 * timestamp and a nonce against replay.
 */
const RECORDING_REQUEST_HEADERS = {
    userId: 'x-streamrtv-user-id',
    timestamp: 'x-streamrtv-timestamp',
    nonce: 'x-streamrtv-nonce',
    signature: 'x-streamrtv-signature',
};
function canonicalRecordingRequestPayload(fields) {
    return utf8([
        'streamrtv-recording-v1',
        fields.userId.toLowerCase(),
        String(fields.timestamp),
        fields.nonce,
        fields.method.toUpperCase(),
        fields.path,
        fields.bodySha256Hex,
    ].join('\n'));
}
async function signRecordingRequest(request) {
    const timestamp = request.now ?? Date.now();
    const nonce = request.nonce ?? crypto.randomUUID();
    const payload = canonicalRecordingRequestPayload({
        userId: request.userId,
        timestamp,
        nonce,
        method: request.method,
        path: request.path,
        bodySha256Hex: await sha256Hex(request.body),
    });
    const signature = await request.sign(payload);
    return {
        [RECORDING_REQUEST_HEADERS.userId]: request.userId.toLowerCase(),
        [RECORDING_REQUEST_HEADERS.timestamp]: String(timestamp),
        [RECORDING_REQUEST_HEADERS.nonce]: nonce,
        [RECORDING_REQUEST_HEADERS.signature]: bytesToHex(signature),
    };
}
const USER_ID_RE = /^0x[0-9a-f]{66}$/; // compressed P-256 public key
const MAX_NONCE_LENGTH = 64;
const MAX_SIGNATURE_HEX_LENGTH = 160;
/**
 * Pure verification, cheapest checks first. `seenBefore` is the caller's
 * replay cache: it records the key and tells whether it was already there.
 * It may be asynchronous (a store shared by several instances).
 */
async function verifyRecordingRequest(request) {
    const userId = (request.headers[RECORDING_REQUEST_HEADERS.userId] ?? '').toLowerCase();
    const timestamp = Number(request.headers[RECORDING_REQUEST_HEADERS.timestamp]);
    const nonce = request.headers[RECORDING_REQUEST_HEADERS.nonce] ?? '';
    const signatureHex = request.headers[RECORDING_REQUEST_HEADERS.signature] ?? '';
    const deny = (error, reason) => ({ ok: false, error, reason });
    if (!USER_ID_RE.test(userId) ||
        !Number.isFinite(timestamp) ||
        nonce.length === 0 ||
        nonce.length > MAX_NONCE_LENGTH ||
        signatureHex.length === 0 ||
        signatureHex.length > MAX_SIGNATURE_HEX_LENGTH ||
        !/^[0-9a-f]+$/.test(signatureHex) ||
        signatureHex.length % 2 !== 0) {
        return deny('bad_request', 'malformed headers');
    }
    if (Math.abs(request.now - timestamp) > (request.maxClockSkewMs ?? 60_000)) {
        return deny('stale_timestamp', 'clock skew');
    }
    const payload = canonicalRecordingRequestPayload({
        userId,
        timestamp,
        nonce,
        method: request.method,
        path: request.path,
        bodySha256Hex: await sha256Hex(request.body),
    });
    let valid = false;
    try {
        valid = await request.verify(userId, payload, hexToBytes(signatureHex));
    }
    catch {
        valid = false;
    }
    if (!valid) {
        return deny('forbidden', 'signature does not verify');
    }
    // after the signature: an unauthenticated sender must not be able to
    // burn the nonces of a legitimate one
    if (await request.seenBefore?.(`${userId}:${nonce}`, request.now)) {
        return deny('replayed', 'nonce reuse');
    }
    return { ok: true, userId };
}

class RecordingBackendError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'RecordingBackendError';
    }
    /** Whether trying again later can help. */
    get retryable() {
        return (this.status === 0 ||
            this.status === 408 ||
            this.status === 429 ||
            this.status >= 500);
    }
}
/**
 * Client of the recording backend. Writes are signed by the broadcaster;
 * the public listing needs no signature.
 */
class RecordingBackendClient {
    base;
    fetchImpl;
    now;
    constructor(options) {
        this.base = options.backendUrl.replace(/\/$/, '');
        this.fetchImpl = options.fetch ?? ((...args) => fetch(...args));
        this.now = options.now ?? (() => Date.now());
    }
    async request(method, path, body, signer, signal) {
        const bytes = body === undefined ? new Uint8Array() : utf8(JSON.stringify(body));
        const headers = signer === undefined
            ? {}
            : await signRecordingRequest({
                method,
                path,
                body: bytes,
                userId: signer.userId,
                sign: signer.sign,
                now: this.now(),
            });
        if (body !== undefined) {
            headers['content-type'] = 'application/json';
        }
        let response;
        try {
            response = await this.fetchImpl(this.base + path, {
                method,
                headers,
                body: body === undefined ? undefined : bytes,
                signal,
            });
        }
        catch (error) {
            if (error.name === 'AbortError') {
                throw error;
            }
            throw new RecordingBackendError(0, 'unreachable', `recording backend unreachable: ${String(error)}`);
        }
        if (!response.ok) {
            let code = 'error';
            try {
                code =
                    (await response.json()).error ??
                        code;
            }
            catch {
                // no JSON body
            }
            throw new RecordingBackendError(response.status, code, `recording backend: ${method} ${path} -> ${response.status} ${code}`);
        }
        if (response.status === 204) {
            return undefined;
        }
        return (await response.json());
    }
    /** Announces a recording. Succeeds if it was announced before. */
    async createRecording(signer, recording) {
        try {
            await this.request('POST', '/api/v1/recordings', recording, signer);
        }
        catch (error) {
            if (error instanceof RecordingBackendError &&
                error.code === 'exists') {
                return;
            }
            throw error;
        }
    }
    async getUploadUrls(signer, recordingId, streamId, objects) {
        return this.request('POST', `/api/v1/recordings/${recordingId}/upload-urls`, { streamId, objects }, signer);
    }
    async finalizeRecording(signer, recordingId, summary) {
        await this.request('POST', `/api/v1/recordings/${recordingId}/finalize`, summary, signer);
    }
    async deleteRecording(signer, recordingId, streamId) {
        await this.request('DELETE', `/api/v1/recordings/${recordingId}?streamId=${encodeURIComponent(streamId)}`, undefined, signer);
    }
    async listPublicRecordings(broadcasterUserId, streamId, signal) {
        const result = await this.request('GET', `/api/v1/streams/${broadcasterUserId.toLowerCase()}/recordings?streamId=${encodeURIComponent(streamId)}`, undefined, undefined, signal);
        return result.recordings;
    }
    async listPrivateRecordings(signer, streamId, signal) {
        const result = await this.request('POST', '/api/v1/streams/private-recordings', { streamId }, signer, signal);
        return result.recordings;
    }
}
/** Where the objects of a recording are read from. */
function recordingObjectUrl(storageUrl, broadcasterUserId, recordingId, name) {
    return `${storageUrl.replace(/\/$/, '')}/recordings/${broadcasterUserId.toLowerCase()}/${recordingId}/${name}`;
}

/**
 * Key handling of a recording.
 *
 * The media of a recording is the wire packets, so it stays under the
 * stream key. The index (and the thumbnail) are encrypted under a separate
 * random key, the index key, which is stored wrapped in `keys.json`:
 *  - always for the broadcaster, under a key derived from the broadcaster's
 *    private key, so the host can open the recording on any device that
 *    holds the broadcast;
 *  - for public recordings also under a key derived from the stream key,
 *    so that everyone who has the stream link can open it.
 * A private recording is shared by putting the index key in a link.
 */
const GCM_NONCE_BYTES = 12;
const INDEX_KEY_BYTES = 32;
const HKDF_INFO_PREFIX = 'streamrtv-recording-index-v1';
function generateIndexKey() {
    return randomBytes(INDEX_KEY_BYTES);
}
function importIndexKey(indexKey) {
    return crypto.subtle.importKey('raw', asBufferSource(indexKey), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
async function deriveWrappingKey(secret, scheme, recordingId) {
    const material = await crypto.subtle.importKey('raw', asBufferSource(secret), 'HKDF', false, ['deriveKey']);
    return crypto.subtle.deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: asBufferSource(utf8(recordingId)),
        info: asBufferSource(utf8(`${HKDF_INFO_PREFIX}:${scheme}`)),
    }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
/** AES-GCM with a random nonce; output is nonce | ciphertext | tag. */
async function sealWithKey(key, plaintext, additionalData) {
    const nonce = randomBytes(GCM_NONCE_BYTES);
    const sealed = await crypto.subtle.encrypt({
        name: 'AES-GCM',
        iv: asBufferSource(nonce),
        additionalData: asBufferSource(additionalData),
    }, key, asBufferSource(plaintext));
    return concatBytes([nonce, new Uint8Array(sealed)]);
}
async function openWithKey(key, sealed, additionalData) {
    if (sealed.byteLength < GCM_NONCE_BYTES + 16) {
        throw new Error('openWithKey: input too short');
    }
    const plain = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: asBufferSource(sealed.subarray(0, GCM_NONCE_BYTES)),
        additionalData: asBufferSource(additionalData),
    }, key, asBufferSource(sealed.subarray(GCM_NONCE_BYTES)));
    return new Uint8Array(plain);
}
/**
 * Builds keys.json. `secrets.streamKey` is only passed for public
 * recordings: leaving it out is what makes a recording private.
 */
async function buildKeysFile(recordingId, indexKey, secrets) {
    const wraps = [];
    const schemes = [
        ['broadcaster', secrets.broadcasterPrivateKey],
        ['stream', secrets.streamKey],
    ];
    for (const [scheme, secret] of schemes) {
        if (secret === undefined) {
            continue;
        }
        const wrappingKey = await deriveWrappingKey(secret, scheme, recordingId);
        const sealed = await sealWithKey(wrappingKey, indexKey, utf8(`${recordingId}:${scheme}`));
        wraps.push({ scheme, wrapped: bytesToBase64Url(sealed) });
    }
    if (wraps.length === 0) {
        throw new Error('buildKeysFile: no secret to wrap the index key with');
    }
    return { v: 1, recordingId, wraps };
}
/**
 * Recovers the index key with whichever secret the caller holds. Returns
 * undefined when none of them opens the file.
 */
async function openKeysFile(file, secrets) {
    if (file.v !== 1) {
        throw new Error(`openKeysFile: unknown version ${String(file.v)}`);
    }
    const bySecret = [
        ['broadcaster', secrets.broadcasterPrivateKey],
        ['stream', secrets.streamKey],
    ];
    for (const [scheme, secret] of bySecret) {
        const wrap = file.wraps.find((w) => w.scheme === scheme);
        if (secret === undefined || wrap === undefined) {
            continue;
        }
        try {
            const wrappingKey = await deriveWrappingKey(secret, scheme, file.recordingId);
            return await openWithKey(wrappingKey, base64UrlToBytes(wrap.wrapped), utf8(`${file.recordingId}:${scheme}`));
        }
        catch {
            // wrong secret for this wrap: try the next one
        }
    }
    return undefined;
}

const RECORDING_INDEX_VERSION = 1;
const TRAILER_MAGIC = utf8('STVT');
const INDEX_MAGIC = utf8('STVI');
const TRAILER_FOOTER_BYTES = 8; // u32 sealed length + magic
const COMPRESSION_NONE = 0;
const COMPRESSION_GZIP = 1;
/** Object names inside a recording's storage prefix. */
const RECORDING_KEYS_OBJECT = 'keys.json';
const RECORDING_INDEX_OBJECT = 'index.enc';
const RECORDING_THUMBNAIL_OBJECT = 'thumbnail.enc';
function segmentObjectName(index) {
    return `seg-${String(index).padStart(6, '0')}.bin`;
}
function trailerAdditionalData(recordingId, segmentIndex) {
    return utf8(`streamrtv-recording-trailer-v1:${recordingId}:${segmentIndex}`);
}
/**
 * A segment object: the wire packets, then the segment's own index entry
 * (encrypted under the index key), then a footer that locates the entry:
 *
 *   [packet region][sealed trailer][u32 sealed length]["STVT"]
 *
 * The trailer makes every uploaded segment self-describing, so nothing
 * that grows is ever uploaded twice while recording.
 */
async function encodeSegmentObject(packetRegion, trailer, indexKey) {
    if (!trailer.entry) {
        throw new Error('encodeSegmentObject: trailer has no entry');
    }
    const sealed = await sealWithKey(indexKey, RecordingSegmentTrailer.toBinary(trailer), trailerAdditionalData(trailer.recordingId, trailer.entry.index));
    return concatBytes([
        packetRegion,
        sealed,
        writeU32(sealed.byteLength),
        TRAILER_MAGIC,
    ]);
}
function locateTrailer(object) {
    const footer = object.byteLength - TRAILER_FOOTER_BYTES;
    if (footer < 0 || !bytesEqual(object.subarray(footer + 4), TRAILER_MAGIC)) {
        throw new Error('segment object has no trailer');
    }
    const sealedLength = readU32(object, footer);
    const start = footer - sealedLength;
    if (start < 0) {
        throw new Error('segment object trailer is truncated');
    }
    return { start, end: footer };
}
/** The wire packets of a segment object, without the trailer. */
function packetRegionOf(object) {
    return object.subarray(0, locateTrailer(object).start);
}
async function readSegmentTrailer(object, recordingId, segmentIndex, indexKey) {
    const { start, end } = locateTrailer(object);
    const plain = await openWithKey(indexKey, object.subarray(start, end), trailerAdditionalData(recordingId, segmentIndex));
    return RecordingSegmentTrailer.fromBinary(plain);
}
async function pipeThrough(bytes, transform) {
    const stream = new Blob([asBufferSource(bytes)])
        .stream()
        .pipeThrough(transform);
    return new Uint8Array(await new Response(stream).arrayBuffer());
}
/**
 * The consolidated index of a recording, written once when it ends:
 *
 *   "STVI" | u8 version | u8 compression | u32 sealed length
 *   | sealed(gzip(RecordingIndex)) | u16 signature length | signature
 *
 * The signature is the broadcaster's, over everything before it, so a
 * viewer can tell that the index (and through the hashes in it, the media)
 * was written by the broadcaster.
 */
async function encodeIndex(index, indexKey, sign) {
    let body = RecordingIndex.toBinary(index);
    let compression = COMPRESSION_NONE;
    if (typeof CompressionStream === 'function') {
        body = await pipeThrough(body, new CompressionStream('gzip'));
        compression = COMPRESSION_GZIP;
    }
    const header = concatBytes([
        INDEX_MAGIC,
        Uint8Array.of(RECORDING_INDEX_VERSION, compression),
    ]);
    const sealed = await sealWithKey(indexKey, body, header);
    const signedPart = concatBytes([
        header,
        writeU32(sealed.byteLength),
        sealed,
    ]);
    const signature = await sign(signedPart);
    if (signature.byteLength > 0xffff) {
        throw new Error('encodeIndex: signature too long');
    }
    return concatBytes([
        signedPart,
        Uint8Array.of(signature.byteLength >> 8, signature.byteLength & 0xff),
        signature,
    ]);
}
async function decodeIndex(bytes, indexKey, 
/** omit only where the signature was already checked */
verify) {
    const headerLength = INDEX_MAGIC.byteLength + 2;
    if (bytes.byteLength < headerLength + 4 ||
        !bytesEqual(bytes.subarray(0, INDEX_MAGIC.byteLength), INDEX_MAGIC)) {
        throw new Error('decodeIndex: not a recording index');
    }
    const version = bytes[INDEX_MAGIC.byteLength];
    const compression = bytes[INDEX_MAGIC.byteLength + 1];
    if (version !== RECORDING_INDEX_VERSION) {
        throw new Error(`decodeIndex: unknown version ${String(version)}`);
    }
    const sealedLength = readU32(bytes, headerLength);
    const sealedStart = headerLength + 4;
    const sealedEnd = sealedStart + sealedLength;
    if (sealedEnd + 2 > bytes.byteLength) {
        throw new Error('decodeIndex: truncated');
    }
    if (verify) {
        const signatureLength = ((bytes[sealedEnd] ?? 0) << 8) | (bytes[sealedEnd + 1] ?? 0);
        const signature = bytes.subarray(sealedEnd + 2, sealedEnd + 2 + signatureLength);
        const valid = signature.byteLength === signatureLength &&
            (await verify.verify(verify.broadcasterUserId, bytes.subarray(0, sealedEnd), signature));
        if (!valid) {
            throw new Error('decodeIndex: signature does not verify');
        }
    }
    let body = await openWithKey(indexKey, bytes.subarray(sealedStart, sealedEnd), bytes.subarray(0, headerLength));
    if (compression === COMPRESSION_GZIP) {
        body = await pipeThrough(body, new DecompressionStream('gzip'));
    }
    else if (compression !== COMPRESSION_NONE) {
        throw new Error(`decodeIndex: unknown compression ${String(compression)}`);
    }
    const index = RecordingIndex.fromBinary(body);
    if (verify &&
        index.broadcasterUserId.toLowerCase() !==
            verify.broadcasterUserId.toLowerCase()) {
        throw new Error('decodeIndex: index belongs to another broadcaster');
    }
    return index;
}

/** Big-endian writer for ISO base media file format boxes. */
class ByteWriter {
    parts = [];
    u8(value) {
        this.parts.push(Uint8Array.of(value & 0xff));
        return this;
    }
    u16(value) {
        const out = new Uint8Array(2);
        new DataView(out.buffer).setUint16(0, value, false);
        this.parts.push(out);
        return this;
    }
    i16(value) {
        const out = new Uint8Array(2);
        new DataView(out.buffer).setInt16(0, value, false);
        this.parts.push(out);
        return this;
    }
    u24(value) {
        this.parts.push(Uint8Array.of((value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff));
        return this;
    }
    u32(value) {
        const out = new Uint8Array(4);
        new DataView(out.buffer).setUint32(0, value, false);
        this.parts.push(out);
        return this;
    }
    i32(value) {
        const out = new Uint8Array(4);
        new DataView(out.buffer).setInt32(0, value, false);
        this.parts.push(out);
        return this;
    }
    u64(value) {
        if (!Number.isSafeInteger(value) || value < 0) {
            throw new Error(`u64: ${String(value)} is not a safe integer`);
        }
        const out = new Uint8Array(8);
        new DataView(out.buffer).setBigUint64(0, BigInt(value), false);
        this.parts.push(out);
        return this;
    }
    ascii(text) {
        const out = new Uint8Array(text.length);
        for (let i = 0; i < text.length; i++) {
            out[i] = text.charCodeAt(i) & 0x7f;
        }
        this.parts.push(out);
        return this;
    }
    zeros(count) {
        this.parts.push(new Uint8Array(count));
        return this;
    }
    bytes(value) {
        this.parts.push(value);
        return this;
    }
    toBytes() {
        return concatBytes(this.parts);
    }
}
function box(type, ...payload) {
    if (type.length !== 4) {
        throw new Error(`box: type "${type}" is not four characters`);
    }
    const body = concatBytes(payload);
    return new ByteWriter()
        .u32(8 + body.byteLength)
        .ascii(type)
        .bytes(body)
        .toBytes();
}
function fullBox(type, version, flags, ...payload) {
    return box(type, new ByteWriter().u8(version).u24(flags).toBytes(), ...payload);
}
/** Header of an mdat box that uses the 64-bit size form. */
const LARGE_MDAT_HEADER_BYTES = 16;
function largeMdatHeader(payloadBytes) {
    return new ByteWriter()
        .u32(1)
        .ascii('mdat')
        .u64(LARGE_MDAT_HEADER_BYTES + payloadBytes)
        .toBytes();
}

/**
 * Lays a recording part out as a progressive MP4 without touching media.
 *
 * The file is `ftyp | moov | mdat`. Everything the `moov` needs (sample
 * sizes, times, keyframes) comes from the recording index, so the header
 * can be produced before a single segment is downloaded. The `mdat` is
 * described by a chunk map: for every chunk of the file, which samples of
 * which segment it holds. A reader resolves byte ranges through that map
 * and loads only the segments a range touches.
 */
const MOVIE_TIMESCALE = 1000;
const VIDEO_TIMESCALE = 90_000;
const DEFAULT_VIDEO_FRAME_TICKS = 3000; // 1/30 s
/** an audio timestamp this far from the expected one counts as a gap */
const AUDIO_GAP_FRAMES = 1.5;
const AUDIO_FRAMES_PER_CHUNK = 50;
function expandTimes(startUs, deltas) {
    const times = [];
    let t = startUs;
    for (let i = 0; i < deltas.length; i++) {
        t += i === 0 ? 0 : (deltas[i] ?? 0);
        times.push(t);
    }
    return times;
}
function prefixSums(sizes) {
    const sums = [0];
    for (const size of sizes) {
        sums.push((sums[sums.length - 1] ?? 0) + size);
    }
    return sums;
}
function toVideoTicks(us) {
    return Math.round((us * 9) / 100);
}
function median(values) {
    if (values.length === 0) {
        return undefined;
    }
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
}
function runLength(values) {
    const runs = [];
    for (const value of values) {
        const last = runs[runs.length - 1];
        if (last && last[1] === value) {
            last[0] += 1;
        }
        else {
            runs.push([1, value]);
        }
    }
    return runs;
}
function collectVideo(part) {
    const samples = [];
    part.segments.forEach((entry, segment) => {
        const times = expandTimes(entry.videoStartUs, entry.videoTsDeltasUs);
        const keys = new Set(entry.videoKeyIndexes);
        entry.videoSizes.forEach((size, index) => {
            samples.push({
                segment,
                index,
                size,
                tsUs: times[index] ?? entry.videoStartUs,
                key: keys.has(index),
            });
        });
    });
    return samples;
}
/**
 * Puts the audio on a constant cadence that starts at t0: audio before t0
 * is dropped, gaps (and a late start) are filled with silence frames. With
 * no silence frame available a gap extends the previous frame instead.
 */
function collectAudio(part, audio, t0Us) {
    const frameUs = audio.frameDurationUs;
    const frameTicks = Math.round((audio.sampleRate * frameUs) / 1_000_000);
    const silenceBytes = audio.silenceFrame.byteLength;
    const items = [];
    let expectedUs = t0Us;
    part.segments.forEach((entry, segment) => {
        const times = expandTimes(entry.audioStartUs, entry.audioTsDeltasUs);
        entry.audioSizes.forEach((size, index) => {
            const tsUs = times[index] ?? entry.audioStartUs;
            if (tsUs < expectedUs - frameUs / 2) {
                return; // before t0, or overlapping what was already placed
            }
            const gapUs = tsUs - expectedUs;
            if (gapUs > frameUs * AUDIO_GAP_FRAMES) {
                const missing = Math.round(gapUs / frameUs);
                const previous = items[items.length - 1];
                if (silenceBytes > 0) {
                    for (let i = 0; i < missing; i++) {
                        items.push({
                            index: 0,
                            size: silenceBytes,
                            timeUs: expectedUs - t0Us + i * frameUs,
                            duration: frameTicks,
                        });
                    }
                }
                else if (previous) {
                    previous.duration += missing * frameTicks;
                }
                // without a previous frame and without silence the track
                // simply starts late by less than the player can notice
                expectedUs += missing * frameUs;
            }
            items.push({
                segment,
                index,
                size,
                timeUs: expectedUs - t0Us,
                duration: frameTicks,
            });
            expectedUs += frameUs;
        });
    });
    return items;
}
function timeVideo(samples, t0Us) {
    const pts = samples.map((s) => toVideoTicks(s.tsUs - t0Us));
    const reordered = pts.some((value, i) => i > 0 && value < (pts[i - 1] ?? 0));
    let decodeTicks = pts;
    let compositionOffsets;
    if (reordered) {
        const sorted = [...pts].sort((a, b) => a - b);
        let shift = 0;
        sorted.forEach((value, i) => {
            shift = Math.max(shift, value - (pts[i] ?? 0));
        });
        decodeTicks = sorted.map((value) => value - shift);
        compositionOffsets = pts.map((value, i) => value - (decodeTicks[i] ?? 0));
    }
    const deltas = [];
    for (let i = 0; i + 1 < decodeTicks.length; i++) {
        deltas.push(Math.max(1, (decodeTicks[i + 1] ?? 0) - (decodeTicks[i] ?? 0)));
    }
    const last = median(deltas) ?? DEFAULT_VIDEO_FRAME_TICKS;
    const durations = samples.length > 0 ? [...deltas, last] : [];
    return {
        durations,
        compositionOffsets,
        decodeTicks,
        totalTicks: durations.reduce((sum, d) => sum + d, 0),
    };
}
function rotationMatrix(video) {
    const one = 0x0001_0000;
    const w = video.codedWidth * one;
    const h = video.codedHeight * one;
    // a, b, u, c, d, v, x, y, w: clockwise rotation, moved back into the
    // positive quadrant
    switch (((video.rotation % 360) + 360) % 360) {
        case 90:
            return [0, one, 0, -one, 0, 0, h, 0, 0x4000_0000];
        case 180:
            return [-one, 0, 0, 0, -one, 0, w, h, 0x4000_0000];
        case 270:
            return [0, -one, 0, one, 0, 0, 0, w, 0x4000_0000];
        default:
            return [one, 0, 0, 0, one, 0, 0, 0, 0x4000_0000];
    }
}
function videoSampleEntry(video) {
    const codec = video.codec.toLowerCase();
    let type;
    let configType;
    if (codec.startsWith('hvc1') || codec.startsWith('hev1')) {
        type = 'hvc1';
        configType = 'hvcC';
    }
    else if (codec.startsWith('avc1') || codec.startsWith('avc3')) {
        type = 'avc1';
        configType = 'avcC';
    }
    else {
        throw new Error(`recording layout: unsupported video codec ${codec}`);
    }
    if (video.extradata.byteLength === 0) {
        throw new Error('recording layout: video has no decoder configuration');
    }
    const entry = new ByteWriter()
        .zeros(6)
        .u16(1) // data reference index
        .zeros(16)
        .u16(video.codedWidth)
        .u16(video.codedHeight)
        .u32(0x0048_0000)
        .u32(0x0048_0000)
        .u32(0)
        .u16(1) // frames per sample
        .zeros(32) // compressor name
        .u16(0x0018)
        .i16(-1)
        .toBytes();
    return box(type, entry, box(configType, video.extradata));
}
function audioSampleEntry(audio) {
    if (audio.codec.toLowerCase() !== 'opus') {
        throw new Error(`recording layout: unsupported audio codec ${audio.codec}`);
    }
    const entry = new ByteWriter()
        .zeros(6)
        .u16(1) // data reference index
        .zeros(8)
        .u16(audio.channels)
        .u16(16)
        .zeros(4)
        .u32(audio.sampleRate * 0x1_0000)
        .toBytes();
    const dOps = new ByteWriter()
        .u8(0) // version
        .u8(audio.channels)
        .u16(audio.preSkip)
        .u32(audio.sampleRate)
        .i16(0) // output gain
        .u8(0) // channel mapping family: mono or stereo
        .toBytes();
    return box('Opus', entry, box('dOps', dOps));
}
function sampleTable(sampleEntry, tables, syncSamples, compositionOffsets) {
    const stsd = fullBox('stsd', 0, 0, new ByteWriter().u32(1).toBytes(), sampleEntry);
    const sttsRuns = runLength(tables.durations);
    const stts = new ByteWriter().u32(sttsRuns.length);
    for (const [count, value] of sttsRuns) {
        stts.u32(count).u32(value);
    }
    const parts = [stsd, fullBox('stts', 0, 0, stts.toBytes())];
    if (compositionOffsets) {
        const runs = runLength(compositionOffsets);
        const ctts = new ByteWriter().u32(runs.length);
        for (const [count, value] of runs) {
            ctts.u32(count).u32(value);
        }
        parts.push(fullBox('ctts', 0, 0, ctts.toBytes()));
    }
    if (syncSamples) {
        const stss = new ByteWriter().u32(syncSamples.length);
        for (const sample of syncSamples) {
            stss.u32(sample);
        }
        parts.push(fullBox('stss', 0, 0, stss.toBytes()));
    }
    // sample to chunk: one entry whenever the samples per chunk change
    const stscEntries = [];
    tables.chunkSampleCounts.forEach((count, i) => {
        const last = stscEntries[stscEntries.length - 1];
        if (!last || last[1] !== count) {
            stscEntries.push([i + 1, count]);
        }
    });
    const stsc = new ByteWriter().u32(stscEntries.length);
    for (const [firstChunk, samples] of stscEntries) {
        stsc.u32(firstChunk).u32(samples).u32(1);
    }
    parts.push(fullBox('stsc', 0, 0, stsc.toBytes()));
    const stsz = new ByteWriter().u32(0).u32(tables.sizes.length);
    for (const size of tables.sizes) {
        stsz.u32(size);
    }
    parts.push(fullBox('stsz', 0, 0, stsz.toBytes()));
    const co64 = new ByteWriter().u32(tables.chunkOffsets.length);
    for (const offset of tables.chunkOffsets) {
        co64.u64(offset);
    }
    parts.push(fullBox('co64', 0, 0, co64.toBytes()));
    return box('stbl', ...parts);
}
function track(options) {
    const movieDuration = Math.round((options.durationTicks * MOVIE_TIMESCALE) / options.timescale);
    const tkhd = new ByteWriter()
        .u64(0) // creation time
        .u64(0) // modification time
        .u32(options.id)
        .u32(0)
        .u64(movieDuration)
        .zeros(8)
        .i16(0) // layer
        .i16(0) // alternate group
        .i16(options.handler === 'soun' ? 0x0100 : 0) // volume
        .u16(0);
    for (const value of options.matrix) {
        tkhd.i32(value);
    }
    tkhd.u32(options.width * 0x1_0000).u32(options.height * 0x1_0000);
    const mdhd = new ByteWriter()
        .u64(0)
        .u64(0)
        .u32(options.timescale)
        .u64(options.durationTicks)
        .u16(0x55c4) // language: und
        .u16(0)
        .toBytes();
    const hdlr = new ByteWriter()
        .u32(0)
        .ascii(options.handler)
        .zeros(12)
        .ascii(options.handler === 'vide' ? 'VideoHandler' : 'SoundHandler')
        .u8(0)
        .toBytes();
    const mediaHeader = options.handler === 'vide'
        ? fullBox('vmhd', 0, 1, new ByteWriter().zeros(8).toBytes())
        : fullBox('smhd', 0, 0, new ByteWriter().zeros(4).toBytes());
    const dinf = box('dinf', fullBox('dref', 0, 0, new ByteWriter().u32(1).toBytes(), fullBox('url ', 0, 1)));
    return box('trak', 
    // enabled, in movie, in preview
    fullBox('tkhd', 1, 7, tkhd.toBytes()), box('mdia', fullBox('mdhd', 1, 0, mdhd), fullBox('hdlr', 0, 0, hdlr), box('minf', mediaHeader, dinf, options.sampleTable)));
}
const IDENTITY_MATRIX = [0x1_0000, 0, 0, 0, 0x1_0000, 0, 0, 0, 0x4000_0000];
function fileType(video) {
    const brands = ['isom', 'iso2', 'mp41'];
    if (video?.codec.toLowerCase().startsWith('avc')) {
        brands.splice(2, 0, 'avc1');
    }
    const body = new ByteWriter().ascii('isom').u32(0x200);
    for (const brand of brands) {
        body.ascii(brand);
    }
    return box('ftyp', body.toBytes());
}
/** Builds the layout of one part from its index alone. */
function buildPartLayout(part) {
    const videoSamples = part.video ? collectVideo(part) : [];
    const firstAudioUs = part.segments.find((entry) => entry.audioSizes.length > 0)?.audioStartUs;
    const t0Us = videoSamples[0]?.tsUs ?? firstAudioUs ?? 0;
    const audioItems = part.audio ? collectAudio(part, part.audio, t0Us) : [];
    const videoTiming = timeVideo(videoSamples, t0Us);
    const audioSums = part.segments.map((entry) => prefixSums(entry.audioSizes));
    const videoSums = part.segments.map((entry) => prefixSums(entry.videoSizes));
    const planned = [];
    const groups = [];
    videoSamples.forEach((sample, position) => {
        const last = groups[groups.length - 1];
        const previous = videoSamples[position - 1];
        if (!last || sample.key || previous?.segment !== sample.segment) {
            groups.push({ first: position, count: 1 });
        }
        else {
            last.count += 1;
        }
    });
    let audioPosition = 0;
    const flushAudioUntil = (limitUs) => {
        while (audioPosition < audioItems.length) {
            const first = audioItems[audioPosition];
            if (!first || first.timeUs >= limitUs) {
                return;
            }
            let count = 0;
            let byteLength = 0;
            while (audioPosition + count < audioItems.length) {
                const item = audioItems[audioPosition + count];
                if (!item ||
                    item.timeUs >= limitUs ||
                    count >= AUDIO_FRAMES_PER_CHUNK ||
                    item.segment !== first.segment ||
                    (item.segment !== undefined &&
                        item.index !== first.index + count)) {
                    break;
                }
                byteLength += item.size;
                count += 1;
            }
            planned.push({
                track: 'audio',
                byteLength,
                sampleCount: count,
                source: first.segment === undefined
                    ? { kind: 'silence', frameCount: count }
                    : {
                        kind: 'segment',
                        segment: first.segment,
                        track: 'audio',
                        firstSample: first.index,
                        sampleCount: count,
                        byteOffsetInTrack: audioSums[first.segment]?.[first.index] ?? 0,
                    },
            });
            audioPosition += count;
        }
    };
    groups.forEach((group, g) => {
        const first = videoSamples[group.first];
        if (!first) {
            return;
        }
        let byteLength = 0;
        for (let i = 0; i < group.count; i++) {
            byteLength += videoSamples[group.first + i]?.size ?? 0;
        }
        planned.push({
            track: 'video',
            byteLength,
            sampleCount: group.count,
            source: {
                kind: 'segment',
                segment: first.segment,
                track: 'video',
                firstSample: first.index,
                sampleCount: group.count,
                byteOffsetInTrack: videoSums[first.segment]?.[first.index] ?? 0,
            },
        });
        const next = groups[g + 1];
        const limitTicks = next === undefined
            ? Number.POSITIVE_INFINITY
            : (videoTiming.decodeTicks[next.first] ?? 0);
        flushAudioUntil((limitTicks * 100) / 9);
    });
    flushAudioUntil(Number.POSITIVE_INFINITY);
    // --- tables ---
    const videoTables = {
        sizes: videoSamples.map((s) => s.size),
        durations: videoTiming.durations,
        chunkSampleCounts: [],
        chunkOffsets: [],
    };
    const audioTables = {
        sizes: audioItems.map((item) => item.size),
        durations: audioItems.map((item) => item.duration),
        chunkSampleCounts: [],
        chunkOffsets: [],
    };
    for (const chunk of planned) {
        const tables = chunk.track === 'video' ? videoTables : audioTables;
        tables.chunkSampleCounts.push(chunk.sampleCount);
        tables.chunkOffsets.push(0);
    }
    const audioTicks = audioTables.durations.reduce((sum, d) => sum + d, 0);
    const audioTimescale = part.audio?.sampleRate ?? 48_000;
    const videoUs = (videoTiming.totalTicks * 100) / 9;
    const audioUs = (audioTicks * 1_000_000) / audioTimescale;
    const durationUs = Math.round(Math.max(videoUs, audioUs));
    const buildHeader = () => {
        const tracks = [];
        if (part.video && videoSamples.length > 0) {
            const syncSamples = [];
            videoSamples.forEach((sample, i) => {
                if (sample.key) {
                    syncSamples.push(i + 1);
                }
            });
            tracks.push(track({
                id: 1,
                handler: 'vide',
                timescale: VIDEO_TIMESCALE,
                durationTicks: videoTiming.totalTicks,
                matrix: rotationMatrix(part.video),
                width: part.video.codedWidth,
                height: part.video.codedHeight,
                sampleTable: sampleTable(videoSampleEntry(part.video), videoTables, syncSamples, videoTiming.compositionOffsets),
            }));
        }
        if (part.audio && audioItems.length > 0) {
            tracks.push(track({
                id: 2,
                handler: 'soun',
                timescale: audioTimescale,
                durationTicks: audioTicks,
                matrix: IDENTITY_MATRIX,
                width: 0,
                height: 0,
                sampleTable: sampleTable(audioSampleEntry(part.audio), audioTables, undefined, undefined),
            }));
        }
        const mvhd = new ByteWriter()
            .u64(0)
            .u64(0)
            .u32(MOVIE_TIMESCALE)
            .u64(Math.round(durationUs / 1000))
            .u32(0x0001_0000) // rate
            .u16(0x0100) // volume
            .zeros(10);
        for (const value of IDENTITY_MATRIX) {
            mvhd.i32(value);
        }
        mvhd.zeros(24).u32(3); // next track id
        const payloadBytes = planned.reduce((sum, c) => sum + c.byteLength, 0);
        return concatBytes([
            fileType(part.video),
            box('moov', fullBox('mvhd', 1, 0, mvhd.toBytes()), ...tracks),
            largeMdatHeader(payloadBytes),
        ]);
    };
    // the header's length does not depend on the offsets (co64 entries have
    // a fixed width): measure it, then place the chunks
    const headerLength = buildHeader().byteLength;
    const chunks = [];
    let offset = headerLength;
    let videoChunk = 0;
    let audioChunk = 0;
    for (const chunk of planned) {
        chunks.push({
            fileOffset: offset,
            byteLength: chunk.byteLength,
            source: chunk.source,
        });
        if (chunk.track === 'video') {
            videoTables.chunkOffsets[videoChunk++] = offset;
        }
        else {
            audioTables.chunkOffsets[audioChunk++] = offset;
        }
        offset += chunk.byteLength;
    }
    const header = buildHeader();
    if (header.byteLength !== headerLength) {
        throw new Error('recording layout: header length changed');
    }
    if (headerLength < LARGE_MDAT_HEADER_BYTES) {
        throw new Error('recording layout: header too short');
    }
    return {
        header,
        totalSize: offset,
        durationUs,
        chunks,
        silenceFrame: part.audio?.silenceFrame ?? new Uint8Array(),
        videoSampleCount: videoSamples.length,
        audioSampleCount: audioItems.length,
    };
}
/** Which segments (positions in `part.segments`) a byte range touches. */
function segmentsForRange(layout, start, endInclusive) {
    const segments = new Set();
    for (let i = firstChunkAt(layout, start); i < layout.chunks.length; i++) {
        const chunk = layout.chunks[i];
        if (!chunk || chunk.fileOffset > endInclusive) {
            break;
        }
        if (chunk.source.kind === 'segment') {
            segments.add(chunk.source.segment);
        }
    }
    return [...segments];
}
/** Index of the first chunk that ends after `position`. */
function firstChunkAt(layout, position) {
    let low = 0;
    let high = layout.chunks.length;
    while (low < high) {
        const middle = (low + high) >> 1;
        const chunk = layout.chunks[middle];
        if (chunk && chunk.fileOffset + chunk.byteLength <= position) {
            low = middle + 1;
        }
        else {
            high = middle;
        }
    }
    return low;
}
function silenceBytes(frame, from, to) {
    const out = new Uint8Array(to - from);
    for (let i = 0; i < out.byteLength; i++) {
        out[i] = frame[(from + i) % frame.byteLength] ?? 0;
    }
    return out;
}
/**
 * Produces the bytes `start..endInclusive` of the file, piece by piece.
 * `loadSegment` is called only for segments the range touches, in file
 * order, so the consumer's pull rate bounds how far ahead media is fetched.
 */
async function* readLayoutRange(layout, start, endInclusive, loadSegment) {
    const end = Math.min(endInclusive, layout.totalSize - 1);
    if (start > end) {
        return;
    }
    if (start < layout.header.byteLength) {
        yield layout.header.subarray(start, Math.min(end + 1, layout.header.byteLength));
    }
    for (let i = firstChunkAt(layout, start); i < layout.chunks.length; i++) {
        const chunk = layout.chunks[i];
        if (!chunk || chunk.fileOffset > end) {
            return;
        }
        const from = Math.max(start, chunk.fileOffset) - chunk.fileOffset;
        const to = Math.min(end + 1, chunk.fileOffset + chunk.byteLength) -
            chunk.fileOffset;
        if (to <= from) {
            continue;
        }
        if (chunk.source.kind === 'silence') {
            yield silenceBytes(layout.silenceFrame, from, to);
            continue;
        }
        const tracks = await loadSegment(chunk.source.segment);
        const bytes = tracks[chunk.source.track];
        const base = chunk.source.byteOffsetInTrack;
        if (base + to > bytes.byteLength) {
            throw new Error('recording layout: segment is shorter than its index entry');
        }
        yield bytes.subarray(base + from, base + to);
    }
}
/**
 * Concatenates the samples of a decoded segment per track and checks them
 * against the segment's index entry. A mismatch means the object and the
 * index do not belong together.
 */
function toSegmentTracks(decoded, entry) {
    const check = (samples, sizes, name) => {
        if (samples.length !== sizes.length) {
            throw new Error(`segment ${entry.index}: ${samples.length} ${name} samples, the index lists ${sizes.length}`);
        }
        samples.forEach((sample, i) => {
            if (sample.data.byteLength !== sizes[i]) {
                throw new Error(`segment ${entry.index}: ${name} sample ${i} has another size than in the index`);
            }
        });
        return concatBytes(samples.map((sample) => sample.data));
    };
    return {
        video: check(decoded.video, entry.videoSizes, 'video'),
        audio: check(decoded.audio, entry.audioSizes, 'audio'),
    };
}

// @generated by protobuf-ts 2.11.1
// @generated from protobuf file "messages.proto" (syntax proto3)
// tslint:disable
/**
 * @generated from protobuf enum RecorderAction
 */
var RecorderAction;
(function (RecorderAction) {
    /**
     * @generated from protobuf enum value: PAUSE = 0;
     */
    RecorderAction[RecorderAction["PAUSE"] = 0] = "PAUSE";
    /**
     * @generated from protobuf enum value: RESUME = 1;
     */
    RecorderAction[RecorderAction["RESUME"] = 1] = "RESUME";
    /**
     * @generated from protobuf enum value: STOP = 2;
     */
    RecorderAction[RecorderAction["STOP"] = 2] = "STOP";
})(RecorderAction || (RecorderAction = {}));
/**
 * @generated from protobuf enum TrackType
 */
var TrackType;
(function (TrackType) {
    /**
     * @generated from protobuf enum value: VIDEO = 0;
     */
    TrackType[TrackType["VIDEO"] = 0] = "VIDEO";
    /**
     * @generated from protobuf enum value: AUDIO = 1;
     */
    TrackType[TrackType["AUDIO"] = 1] = "AUDIO";
})(TrackType || (TrackType = {}));
/**
 * @generated from protobuf enum UserRole
 */
var UserRole;
(function (UserRole) {
    /**
     * @generated from protobuf enum value: BANNED = 0;
     */
    UserRole[UserRole["BANNED"] = 0] = "BANNED";
    /**
     * @generated from protobuf enum value: VIEWER = 1;
     */
    UserRole[UserRole["VIEWER"] = 1] = "VIEWER";
    /**
     * @generated from protobuf enum value: SPEAKER = 2;
     */
    UserRole[UserRole["SPEAKER"] = 2] = "SPEAKER";
    /**
     * @generated from protobuf enum value: COHOST = 3;
     */
    UserRole[UserRole["COHOST"] = 3] = "COHOST";
    /**
     * @generated from protobuf enum value: GUEST = 4;
     */
    UserRole[UserRole["GUEST"] = 4] = "GUEST";
    /**
     * @generated from protobuf enum value: CAMERA = 5;
     */
    UserRole[UserRole["CAMERA"] = 5] = "CAMERA";
})(UserRole || (UserRole = {}));
// @generated message type with reflection information, may provide speed optimized methods
class Heartbeat$Type extends MessageType {
    constructor() {
        super("Heartbeat", [
            { no: 1, name: "nodeId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "neighbors", kind: "scalar", repeat: 2 /*RepeatType.UNPACKED*/, T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "downloadRateBytesPerSecond", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "uploadRateBytesPerSecond", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 5, name: "isRecording", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 6, name: "displayName", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 7, name: "webrtcDirectConnectionCount", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 8, name: "webrtcRelayConnectionCount", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Heartbeat
 */
const Heartbeat = new Heartbeat$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Leave$Type extends MessageType {
    constructor() {
        super("Leave", [
            { no: 1, name: "nodeId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Leave
 */
const Leave = new Leave$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Product$Type extends MessageType {
    constructor() {
        super("Product", [
            { no: 1, name: "id", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "name", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "currency", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Product
 */
const Product = new Product$Type();
// @generated message type with reflection information, may provide speed optimized methods
class BroadcastMetadata$Type extends MessageType {
    constructor() {
        super("BroadcastMetadata", [
            { no: 1, name: "viewerCount", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 2, name: "products", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => Product },
            { no: 3, name: "acceptedParticipantType", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "lowQualityPartition", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 5, name: "highQualityAvailable", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message BroadcastMetadata
 */
const BroadcastMetadata = new BroadcastMetadata$Type();
// @generated message type with reflection information, may provide speed optimized methods
class RecorderInstruction$Type extends MessageType {
    constructor() {
        super("RecorderInstruction", [
            { no: 1, name: "action", kind: "enum", T: () => ["RecorderAction", RecorderAction] }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message RecorderInstruction
 */
const RecorderInstruction = new RecorderInstruction$Type();
// @generated message type with reflection information, may provide speed optimized methods
class Message$Type extends MessageType {
    constructor() {
        super("Message", [
            { no: 1, name: "messageId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "mediaPacketFragment", kind: "message", oneof: "body", T: () => MediaPacketFragment },
            { no: 3, name: "heartbeat", kind: "message", oneof: "body", T: () => Heartbeat },
            { no: 4, name: "leave", kind: "message", oneof: "body", T: () => Leave },
            { no: 5, name: "BroadcastMetadata", kind: "message", jsonName: "BroadcastMetadata", oneof: "body", T: () => BroadcastMetadata },
            { no: 6, name: "recorderInstruction", kind: "message", oneof: "body", T: () => RecorderInstruction },
            { no: 7, name: "pluginMessage", kind: "message", oneof: "body", T: () => PluginMessage },
            { no: 8, name: "purchaseClaimedMessage", kind: "message", oneof: "body", T: () => PurchaseClaimedMessage },
            { no: 9, name: "purchaseConfirmedMessage", kind: "message", oneof: "body", T: () => PurchaseConfirmedMessage },
            { no: 10, name: "viewerStreamMessage", kind: "message", oneof: "body", T: () => ViewerStreamMessage },
            { no: 11, name: "permissionRequest", kind: "message", oneof: "body", T: () => PermissionRequest },
            { no: 12, name: "setUserRole", kind: "message", oneof: "body", T: () => SetUserRole },
            { no: 13, name: "privateMessage", kind: "message", oneof: "body", T: () => PrivateMessage },
            { no: 14, name: "trackEnded", kind: "message", oneof: "body", T: () => TrackEnded }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message Message
 */
const Message = new Message$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PluginMessage$Type extends MessageType {
    constructor() {
        super("PluginMessage", [
            { no: 1, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "pluginId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PluginMessage
 */
const PluginMessage = new PluginMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PurchaseClaimedMessage$Type extends MessageType {
    constructor() {
        super("PurchaseClaimedMessage", [
            { no: 1, name: "checkoutSessionId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PurchaseClaimedMessage
 */
const PurchaseClaimedMessage = new PurchaseClaimedMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PurchaseConfirmedMessage$Type extends MessageType {
    constructor() {
        super("PurchaseConfirmedMessage", [
            { no: 1, name: "buyerUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "productId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "merchantId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 4, name: "currency", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 5, name: "priceUnitAmount", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 6, name: "metadataJson", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PurchaseConfirmedMessage
 */
const PurchaseConfirmedMessage = new PurchaseConfirmedMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class VideoFrame$Type extends MessageType {
    constructor() {
        super("VideoFrame", [
            { no: 1, name: "frameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "timestamp", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 3, name: "duration", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 5, name: "isKeyFrame", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 7, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 8, name: "extradata", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 9, name: "codedWidth", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 10, name: "codedHeight", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 11, name: "codec", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 12, name: "isScreenSharing", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 14, name: "rotation", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 15, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 16, name: "done", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 17, name: "annexb", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message VideoFrame
 */
const VideoFrame = new VideoFrame$Type();
// @generated message type with reflection information, may provide speed optimized methods
class AudioFrame$Type extends MessageType {
    constructor() {
        super("AudioFrame", [
            { no: 1, name: "frameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "timestamp", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 3, name: "duration", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 4, name: "isKeyFrame", kind: "scalar", T: 8 /*ScalarType.BOOL*/ },
            { no: 5, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 6, name: "extradata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 7, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 8, name: "done", kind: "scalar", T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message AudioFrame
 */
const AudioFrame = new AudioFrame$Type();
// @generated message type with reflection information, may provide speed optimized methods
class EndedTrack$Type extends MessageType {
    constructor() {
        super("EndedTrack", [
            { no: 1, name: "trackId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "type", kind: "enum", T: () => ["TrackType", TrackType] },
            { no: 3, name: "lastFrameNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message EndedTrack
 */
const EndedTrack = new EndedTrack$Type();
// @generated message type with reflection information, may provide speed optimized methods
class TrackEnded$Type extends MessageType {
    constructor() {
        super("TrackEnded", [
            { no: 1, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 2, name: "track", kind: "message", T: () => EndedTrack }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message TrackEnded
 */
const TrackEnded = new TrackEnded$Type();
// @generated message type with reflection information, may provide speed optimized methods
class MediaPacket$Type extends MessageType {
    constructor() {
        super("MediaPacket", [
            { no: 1, name: "packetNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "videoCodec", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 3, name: "videoMetadata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "videoFrames", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => VideoFrame },
            { no: 5, name: "audioCodec", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ },
            { no: 6, name: "audioMetadata", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 7, name: "audioFrames", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => AudioFrame },
            { no: 8, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ },
            { no: 9, name: "endedTracks", kind: "message", repeat: 2 /*RepeatType.UNPACKED*/, T: () => EndedTrack },
            { no: 10, name: "audioLevel", kind: "scalar", opt: true, T: 2 /*ScalarType.FLOAT*/ },
            { no: 11, name: "isSpeaking", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ },
            { no: 12, name: "lowQualityPartition", kind: "scalar", opt: true, T: 5 /*ScalarType.INT32*/ },
            { no: 13, name: "highQualityAvailable", kind: "scalar", opt: true, T: 8 /*ScalarType.BOOL*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message MediaPacket
 */
const MediaPacket = new MediaPacket$Type();
// @generated message type with reflection information, may provide speed optimized methods
class MediaPacketFragment$Type extends MessageType {
    constructor() {
        super("MediaPacketFragment", [
            { no: 1, name: "packetNumber", kind: "scalar", T: 3 /*ScalarType.INT64*/, L: 0 /*LongType.BIGINT*/ },
            { no: 2, name: "fragmentNumber", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 3, name: "numberOfFragments", kind: "scalar", T: 5 /*ScalarType.INT32*/ },
            { no: 4, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 5, name: "mediaStreamId", kind: "scalar", T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message MediaPacketFragment
 */
const MediaPacketFragment = new MediaPacketFragment$Type();
// @generated message type with reflection information, may provide speed optimized methods
class ViewerStreamMessage$Type extends MessageType {
    constructor() {
        super("ViewerStreamMessage", [
            { no: 1, name: "mediaPacketFragment", kind: "message", T: () => MediaPacketFragment }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message ViewerStreamMessage
 */
const ViewerStreamMessage = new ViewerStreamMessage$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PermissionRequest$Type extends MessageType {
    constructor() {
        super("PermissionRequest", [
            { no: 1, name: "userRole", kind: "enum", T: () => ["UserRole", UserRole] },
            { no: 2, name: "secret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 3, name: "displayName", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PermissionRequest
 */
const PermissionRequest = new PermissionRequest$Type();
// @generated message type with reflection information, may provide speed optimized methods
class SetUserRole$Type extends MessageType {
    constructor() {
        super("SetUserRole", [
            { no: 1, name: "userRole", kind: "enum", T: () => ["UserRole", UserRole] },
            { no: 2, name: "targetUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 3, name: "speakerSecret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 4, name: "hostSecret", kind: "scalar", opt: true, T: 12 /*ScalarType.BYTES*/ },
            { no: 5, name: "oneToOneStreamUrl", kind: "scalar", opt: true, T: 9 /*ScalarType.STRING*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message SetUserRole
 */
const SetUserRole = new SetUserRole$Type();
// @generated message type with reflection information, may provide speed optimized methods
class PrivateMessage$Type extends MessageType {
    constructor() {
        super("PrivateMessage", [
            { no: 1, name: "data", kind: "scalar", T: 12 /*ScalarType.BYTES*/ },
            { no: 2, name: "targetUserId", kind: "scalar", T: 12 /*ScalarType.BYTES*/ }
        ]);
    }
}
/**
 * @generated MessageType for protobuf message PrivateMessage
 */
const PrivateMessage = new PrivateMessage$Type();

/**
 * Version of the stored packet format, written into every recording index.
 * Bump it when the network envelope, the message protobufs or the payload
 * encryption change, and keep decoding the older versions.
 */
const WIRE_FORMAT_VERSION = 1;
/** Length of the IV that prefixes an encrypted payload (Encryption.ts). */
const WIRE_IV_BYTES = 16;
/** Reads the envelope of one published StreamMessage. */
function parseWireMessage(bytes) {
    const message = WireStreamMessage.fromBinary(bytes, {
        readUnknownField: false,
    });
    if (!message.messageId || !message.contentMessage) {
        throw new Error('parseWireMessage: not a content message');
    }
    return {
        partition: message.messageId.streamPartition,
        sequenceNumber: message.messageId.sequenceNumber,
        timestamp: message.messageId.timestamp,
        content: message.contentMessage.content,
    };
}
/** The packet region of a segment object: each message length-prefixed. */
function packWireMessages(messages) {
    const parts = [];
    for (const message of messages) {
        parts.push(writeU32(message.byteLength), message);
    }
    return concatBytes(parts);
}
function unpackWireMessages(region) {
    const messages = [];
    let offset = 0;
    while (offset < region.byteLength) {
        if (offset + 4 > region.byteLength) {
            throw new Error('unpackWireMessages: truncated length prefix');
        }
        const length = readU32(region, offset);
        offset += 4;
        if (offset + length > region.byteLength) {
            throw new Error('unpackWireMessages: truncated message');
        }
        messages.push(region.subarray(offset, offset + length));
        offset += length;
    }
    return messages;
}
function importWireKey(aesKey) {
    return crypto.subtle.importKey('raw', asBufferSource(aesKey), { name: 'AES-CTR' }, false, ['decrypt']);
}
/**
 * Decrypts a published payload. Same cipher as Encryption.ts
 * (AES-256-CTR, the 16-byte IV is the initial counter block and the whole
 * block counts), through WebCrypto so that it runs in a service worker.
 */
async function decryptWireContent(content, key) {
    if (content.byteLength < WIRE_IV_BYTES) {
        throw new Error('decryptWireContent: payload shorter than the IV');
    }
    const plain = await crypto.subtle.decrypt({
        name: 'AES-CTR',
        counter: asBufferSource(content.subarray(0, WIRE_IV_BYTES)),
        length: 128,
    }, key, asBufferSource(content.subarray(WIRE_IV_BYTES)));
    return new Uint8Array(plain);
}
/**
 * Which frames of a packet are samples of the recording. The recorder's
 * index and the decoder must agree on this, so both call it.
 */
function isRecordableFrame(frame) {
    return !frame.done && frame.data.byteLength > 0;
}
/**
 * Turns the packet region of a segment object back into samples: decrypt
 * every message, reassemble the fragments of each packet, read the frames.
 * Message signatures are not verified here: the index carries a hash of the
 * region and is itself signed by the broadcaster.
 */
async function decodeSegment(region, key) {
    const messages = unpackWireMessages(region);
    const payloads = await Promise.all(messages.map((bytes) => decryptWireContent(parseWireMessage(bytes).content, key)));
    const pending = new Map();
    const order = [];
    for (const payload of payloads) {
        const message = Message.fromBinary(payload, {
            readUnknownField: false,
        });
        if (message.body.oneofKind !== 'mediaPacketFragment') {
            continue;
        }
        const fragment = message.body.mediaPacketFragment;
        const id = fragment.packetNumber.toString();
        let packet = pending.get(id);
        if (!packet) {
            packet = {
                fragments: new Array(fragment.numberOfFragments).fill(undefined),
                received: 0,
            };
            pending.set(id, packet);
            order.push(id);
        }
        if (fragment.fragmentNumber < packet.fragments.length &&
            packet.fragments[fragment.fragmentNumber] === undefined) {
            packet.fragments[fragment.fragmentNumber] = fragment.data;
            packet.received += 1;
        }
    }
    const decoded = { video: [], audio: [], packetNumbers: [] };
    for (const id of order) {
        const packet = pending.get(id);
        if (!packet || packet.received !== packet.fragments.length) {
            throw new Error(`decodeSegment: packet ${id} is incomplete`);
        }
        const media = MediaPacket.fromBinary(concatBytes(packet.fragments), { readUnknownField: false });
        decoded.packetNumbers.push(Number(id));
        for (const frame of media.videoFrames) {
            if (isRecordableFrame(frame)) {
                if (frame.annexb) {
                    throw new Error('decodeSegment: Annex B video is not supported');
                }
                decoded.video.push({
                    data: frame.data,
                    timestampUs: Number(frame.timestamp),
                    isKeyFrame: frame.isKeyFrame,
                });
            }
        }
        for (const frame of media.audioFrames) {
            if (isRecordableFrame(frame)) {
                decoded.audio.push({
                    data: frame.data,
                    timestampUs: Number(frame.timestamp),
                    isKeyFrame: true,
                });
            }
        }
    }
    return decoded;
}

export { RECORDING_INDEX_OBJECT, RECORDING_INDEX_VERSION, RECORDING_KEYS_OBJECT, RECORDING_REQUEST_HEADERS, RECORDING_THUMBNAIL_OBJECT, RecordingAudioConfig, RecordingBackendClient, RecordingBackendError, RecordingIndex, RecordingPart, RecordingSegmentEntry, RecordingSegmentTrailer, RecordingVideoConfig, WIRE_FORMAT_VERSION, WireContentMessage, WireMessageId, WireStreamMessage, asBufferSource, base64UrlToBytes, buildKeysFile, buildPartLayout, bytesEqual, bytesToBase64Url, bytesToHex, canonicalRecordingRequestPayload, concatBytes, decodeIndex, decodeSegment, decryptWireContent, encodeIndex, encodeSegmentObject, generateIndexKey, hexToBytes, importIndexKey, importWireKey, isRecordableFrame, openKeysFile, openWithKey, packWireMessages, packetRegionOf, parseWireMessage, randomBytes, readLayoutRange, readSegmentTrailer, readU32, recordingObjectUrl, sealWithKey, segmentObjectName, segmentsForRange, sha256, sha256Hex, signRecordingRequest, toSegmentTracks, unpackWireMessages, utf8, verifyRecordingRequest, writeU32 };
